/**
 * MessageList Component
 *
 * Virtualized message list with Markdown rendering, tool cards, and auto-scroll.
 * Ported from V1 with Tailwind gray-* classes for V2.
 *
 * @module components/session/MessageList
 */

import { useEffect, useRef, useState, useMemo, memo, useCallback, lazy, Suspense, useLayoutEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import TextareaAutosize from 'react-textarea-autosize';
import { useUI } from '../../contexts/UIContext.jsx';
import {
  User, Bot, Terminal, CheckCircle, AlertCircle, Info,
  FileText, Search, FolderSearch, Pencil, FilePlus, Globe,
  ChevronDown, ChevronRight, Play, ArrowDown,
  ClipboardList, Brain, Paperclip, Image, HelpCircle, Zap, Command,
  Copy, Check, MessageCircle, Send, AlertTriangle, StickyNote,
  Plus, UserMinus, Tag, MessageSquare, Trash2, Inbox,
  Lightbulb, Database, Key
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Lazy load SyntaxHighlighter
const SyntaxHighlighter = lazy(() =>
  import('react-syntax-highlighter').then(mod => ({
    default: mod.Prism
  }))
);

import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import RawMessageView from './RawMessageView.jsx';

// ============================================
// Shared Color Classes (used by multiple components)
// ============================================
const COLOR_CLASSES = {
  blue: { bg: 'bg-blue-500/20', border: 'border-blue-500/30', text: 'text-blue-400', glow: 'shadow-blue-500/20' },
  cyan: { bg: 'bg-cyan-500/20', border: 'border-cyan-500/30', text: 'text-cyan-400', glow: 'shadow-cyan-500/20' },
  yellow: { bg: 'bg-yellow-500/20', border: 'border-yellow-500/30', text: 'text-yellow-400', glow: 'shadow-yellow-500/20' },
  orange: { bg: 'bg-orange-500/20', border: 'border-orange-500/30', text: 'text-orange-400', glow: 'shadow-orange-500/20' },
  green: { bg: 'bg-green-500/20', border: 'border-green-500/30', text: 'text-green-400', glow: 'shadow-green-500/20' },
  purple: { bg: 'bg-purple-500/20', border: 'border-purple-500/30', text: 'text-purple-400', glow: 'shadow-purple-500/20' },
  pink: { bg: 'bg-pink-500/20', border: 'border-pink-500/30', text: 'text-pink-400', glow: 'shadow-pink-500/20' },
  indigo: { bg: 'bg-indigo-500/20', border: 'border-indigo-500/30', text: 'text-indigo-400', glow: 'shadow-indigo-500/20' },
  red: { bg: 'bg-red-500/20', border: 'border-red-500/30', text: 'text-red-400', glow: 'shadow-red-500/20' },
  gray: { bg: 'bg-gray-500/20', border: 'border-gray-500/30', text: 'text-gray-400', glow: 'shadow-gray-500/20' },
};

// ============================================
// Safe XML Parser (no Firefox console errors)
// ============================================

/**
 * Safely parse XML-like tags without triggering Firefox console errors.
 * Uses text/html mode which doesn't log parse errors to console.
 * @param {string} xmlString - The XML string to parse
 * @param {string} tagName - The root tag name to look for
 * @returns {Element|null} - The parsed element or null if invalid
 */
function safeParseXml(xmlString, tagName) {
  try {
    // Wrap in a div and parse as HTML to avoid Firefox XML console errors
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${xmlString}</div>`, 'text/html');

    // Find the tag within the parsed content
    const element = doc.querySelector(tagName);
    return element || null;
  } catch {
    return null;
  }
}

// ============================================
// Helper Components
// ============================================

const CopyButton = memo(function CopyButton({ text, variant = 'icon', className = '' }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [text]);

  if (variant === 'pill') {
    return (
      <button
        onClick={handleCopy}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-all duration-200
          ${copied ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 hover:bg-gray-600 text-gray-400 hover:text-gray-200'}
          ${className}`}
        title={copied ? 'Copied!' : 'Copy to clipboard'}
      >
        {copied ? <><Check className="w-3.5 h-3.5" /><span>Copied</span></> : <><Copy className="w-3.5 h-3.5" /><span>Copy</span></>}
      </button>
    );
  }

  return (
    <button
      onClick={handleCopy}
      className={`p-1 rounded transition-all duration-200
        ${copied ? 'text-green-400 bg-green-500/20' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-700'}
        ${className}`}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
    </button>
  );
});

const CodeFallback = memo(function CodeFallback({ children }) {
  return (
    <pre className="bg-gray-900 rounded-lg p-3 text-xs text-gray-300 font-mono overflow-x-auto animate-pulse">
      <code>{children}</code>
    </pre>
  );
});

const LazyCodeBlock = memo(function LazyCodeBlock({ language, style, customStyle, children, showCopy = true, ...props }) {
  const codeString = String(children).replace(/\n$/, '');

  return (
    <div className="relative group">
      {showCopy && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
          <CopyButton text={codeString} variant="pill" />
        </div>
      )}
      <Suspense fallback={<CodeFallback>{children}</CodeFallback>}>
        <SyntaxHighlighter
          language={language}
          style={style}
          customStyle={customStyle}
          {...props}
        >
          {codeString}
        </SyntaxHighlighter>
      </Suspense>
    </div>
  );
});

// ============================================
// Timestamp Formatting
// ============================================

/**
 * Format timestamp for message display
 * @param {number|string|Date} timestamp - Timestamp to format
 * @returns {string} Formatted time string
 */
function formatMessageTime(timestamp) {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  // Format time as HH:MM
  const timeStr = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  });

  // Just now (< 1 min)
  if (diffMins < 1) return 'just now';

  // Minutes ago (< 1 hour)
  if (diffMins < 60) return `${diffMins}m ago`;

  // Today - show time only
  if (diffDays === 0) return timeStr;

  // Yesterday
  if (diffDays === 1) return `yesterday ${timeStr}`;

  // This week (< 7 days)
  if (diffDays < 7) {
    const dayName = date.toLocaleDateString(undefined, { weekday: 'short' });
    return `${dayName} ${timeStr}`;
  }

  // Older - show date
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit'
  }) + ' ' + timeStr;
}

// ============================================
// Content Parsing Helpers
// ============================================

function parseSystemReminders(text) {
  if (!text || typeof text !== 'string') return { content: text || '', reminders: [] };

  const reminders = [];
  // Match <system-reminder> with optional attributes
  const reminderRegex = /<system-reminder[^>]*>([\s\S]*?)<\/system-reminder>/g;
  let match;
  while ((match = reminderRegex.exec(text)) !== null) {
    reminders.push(match[1].trim());
  }

  // Remove all system-reminder tags (with or without attributes)
  const content = text.replace(/<system-reminder[^>]*>[\s\S]*?<\/system-reminder>/g, '').trim();
  return { content, reminders };
}

function parseThinking(text) {
  if (!text || typeof text !== 'string') return { content: text || '', thinking: [] };

  const thinking = [];
  const thinkingRegex = /<thinking>([\s\S]*?)<\/thinking>/g;
  let match;
  while ((match = thinkingRegex.exec(text)) !== null) {
    thinking.push(match[1].trim());
  }

  const content = text.replace(/<thinking>[\s\S]*?<\/thinking>/g, '').trim();
  return { content, thinking };
}

// Helper to extract status tags from content
// Valid modes from backend StatusUpdateSchema (see backend/src/utils/validation.ts)
const VALID_STATUS_MODES = ['ready', 'working', 'focus', 'in_call', 'offline'];

function parseStatusTags(text) {
  if (!text || typeof text !== 'string') return { content: text || '', statusTags: [] };

  const statusTags = [];
  // Match self-closing <status .../> tags with attributes
  const statusRegex = /<status\s+([^/>]*)\/?>/g;
  let match;
  while ((match = statusRegex.exec(text)) !== null) {
    const attrsStr = match[1];
    const attrs = {};
    // Parse attributes like mode="working" project="claude-web-ui"
    const attrRegex = /(\w+)="([^"]*)"/g;
    let attrMatch;
    while ((attrMatch = attrRegex.exec(attrsStr)) !== null) {
      attrs[attrMatch[1]] = attrMatch[2];
    }
    // Only accept tags with valid mode values (filter out documentation examples like "...")
    if (attrs.mode && VALID_STATUS_MODES.includes(attrs.mode)) {
      statusTags.push(attrs);
    }
  }

  const content = text.replace(/<status\s+[^/>]*\/?>/g, '').trim();
  return { content, statusTags };
}

// Helper to extract queue-next tags from content
function parseQueueTags(text) {
  if (!text || typeof text !== 'string') return { content: text || '', queueTags: 0 };

  // Count <queue-next/> or <queue-next /> tags
  const queueRegex = /<queue-next\s*\/?>/g;
  const matches = text.match(queueRegex);
  const queueTags = matches ? matches.length : 0;

  const content = text.replace(/<queue-next\s*\/?>/g, '').trim();
  return { content, queueTags };
}

// ============================================
// System Message Parsing (Session Context, Help, Query)
// ============================================

/**
 * Parse <session-context> tags from messages
 * These are auto-injected after compact and session start
 */
function parseSessionContext(text) {
  if (!text || typeof text !== 'string') return { content: text || '', sessionContext: null };

  const sessionContextRegex = /<session-context>([\s\S]*?)<\/session-context>/i;
  const match = sessionContextRegex.exec(text);

  if (!match) return { content: text, sessionContext: null };

  const innerContent = match[1];

  // Parse agent info - supports both self-closing and content format
  // Try content format first (with description)
  const agentContentMatch = /<agent\s+uuid="([^"]*)"(?:\s+name="([^"]*)")?\s*>([\s\S]*?)<\/agent>/i.exec(innerContent);
  // Fallback to self-closing format
  const agentSelfClosingMatch = /<agent\s+uuid="([^"]*)"(?:\s+name="([^"]*)")?\s*\/>/i.exec(innerContent);
  const agentMatch = agentContentMatch || agentSelfClosingMatch;
  const agentDescription = agentContentMatch ? agentContentMatch[3]?.trim() : null;

  // Parse channels - supports both wrapped (<channels>...) and direct format
  const channels = [];
  // Extract <channels> wrapper content if present, otherwise use innerContent
  const channelsWrapperMatch = /<channels>([\s\S]*?)<\/channels>/i.exec(innerContent);
  const channelsContent = channelsWrapperMatch ? channelsWrapperMatch[1] : innerContent;

  // First try self-closing format with members count attribute (new format)
  const selfClosingChannelRegex = /<channel\s+id="([^"]*)"(?:\s+name="([^"]*)")?(?:\s+display-name="([^"]*)")?(?:\s+members="(\d+)")?\s*\/>/gi;
  let selfClosingMatch;
  while ((selfClosingMatch = selfClosingChannelRegex.exec(channelsContent)) !== null) {
    channels.push({
      id: selfClosingMatch[1],
      name: selfClosingMatch[2],
      displayName: selfClosingMatch[3],
      memberCount: selfClosingMatch[4] ? parseInt(selfClosingMatch[4], 10) : 0,
      members: [] // No detailed members in self-closing format
    });
  }
  // Also try content format (old format with member details)
  const contentChannelRegex = /<channel\s+id="([^"]*)"(?:\s+name="([^"]*)")?(?:\s+display-name="([^"]*)")?\s*>([\s\S]*?)<\/channel>/gi;
  let channelMatch;
  while ((channelMatch = contentChannelRegex.exec(channelsContent)) !== null) {
    const members = [];
    const memberRegex = /<member\s+type="([^"]*)"(?:\s+id="([^"]*)")?(?:\s+name="([^"]*)")?\s*\/?>/gi;
    let memberMatch;
    while ((memberMatch = memberRegex.exec(channelMatch[4])) !== null) {
      members.push({
        type: memberMatch[1],
        id: memberMatch[2],
        name: memberMatch[3]
      });
    }
    channels.push({
      id: channelMatch[1],
      name: channelMatch[2],
      displayName: channelMatch[3],
      memberCount: members.length,
      members
    });
  }

  // Parse role (agent's own role)
  const roleMatch = /<role\s+name="([^"]*)"(?:\s+display-name="([^"]*)")?(?:\s+level="([^"]*)")?\s*>([\s\S]*?)<\/role>/i.exec(innerContent);
  const role = roleMatch ? {
    name: roleMatch[1],
    displayName: roleMatch[2],
    level: roleMatch[3],
    description: roleMatch[4]?.trim() || null
  } : null;

  // Parse team members (from project channel)
  const teamMatch = /<team\s+project="([^"]*)">([\s\S]*?)<\/team>/i.exec(innerContent);
  let team = null;
  if (teamMatch) {
    const teamMembers = [];
    const teamMemberRegex = /<member\s+type="([^"]*)"(?:\s+name="([^"]*)")?(?:\s+role="([^"]*)")?(?:\s+level="([^"]*)")?\s*\/?>/gi;
    let teamMemberMatch;
    while ((teamMemberMatch = teamMemberRegex.exec(teamMatch[2])) !== null) {
      teamMembers.push({
        type: teamMemberMatch[1],
        name: teamMemberMatch[2],
        role: teamMemberMatch[3],
        level: teamMemberMatch[4]
      });
    }
    team = {
      project: teamMatch[1],
      members: teamMembers
    };
  }

  // Parse queue stats
  const queueMatch = /<queue\s+pending="(\d+)"(?:\s+oldest="([^"]*)")?\s*\/>/.exec(innerContent);

  // Parse status
  const statusMatch = /<status\s+mode="([^"]*)"(?:\s+project="([^"]*)")?\s*\/>/.exec(innerContent);

  // Parse hint
  const hintMatch = /<hint>([^<]*)<\/hint>/.exec(innerContent);

  // Parse self-note (agent's saved note before context compaction)
  // Note: self-note may have a saved="..." attribute
  const selfNoteMatch = /<self-note(?:\s+saved="[^"]*")?>([\s\S]*?)<\/self-note>/i.exec(innerContent);

  const sessionContext = {
    agent: agentMatch ? { uuid: agentMatch[1], name: agentMatch[2], description: agentDescription } : null,
    role,
    team,
    channels,
    queue: queueMatch ? { pending: parseInt(queueMatch[1], 10), oldest: queueMatch[2] } : null,
    status: statusMatch ? { mode: statusMatch[1], project: statusMatch[2] } : null,
    hint: hintMatch ? hintMatch[1] : null,
    selfNote: selfNoteMatch ? selfNoteMatch[1].trim() : null
  };

  // Remove both full and self-closing session-context tags
  let content = text.replace(/<session-context>[\s\S]*?<\/session-context>/gi, '');
  // Also remove self-closing <session-context/> tags
  content = content.replace(/<session-context\s*\/>/gi, '');
  // Remove full <self-note>...</self-note> tags (already parsed above)
  content = content.replace(/<self-note(?:\s+[^>]*)?>[\s\S]*?<\/self-note>/gi, '');
  // Also remove self-closing <self-note/> tags
  content = content.replace(/<self-note\s*\/>/gi, '').trim();
  return { content, sessionContext };
}

/**
 * Parse <context-warning> tags from user messages (injected by backend when threshold reached)
 * Format: <context-warning percent="X" threshold="Y">message</context-warning>
 */
function parseContextWarning(text) {
  if (!text || typeof text !== 'string') return { content: text || '', contextWarning: null };

  // Match <context-warning ...>...</context-warning> with flexible attribute order
  const contextWarningRegex = /<context-warning([^>]*)>([\s\S]*?)<\/context-warning>/i;
  const match = contextWarningRegex.exec(text);

  if (!match) return { content: text, contextWarning: null };

  // Extract attributes from the tag (flexible order)
  const attrs = match[1];
  const percentMatch = /percent="(\d+)"/.exec(attrs);
  const thresholdMatch = /threshold="(\d+)"/.exec(attrs);

  const contextWarning = {
    percentUsed: percentMatch ? parseInt(percentMatch[1], 10) : null,
    threshold: thresholdMatch ? parseInt(thresholdMatch[1], 10) : null
  };

  // Remove ALL context-warning tags from content (including self-closing)
  const content = text
    .replace(/<context-warning[^>]*>[\s\S]*?<\/context-warning>/gi, '')
    .replace(/<context-warning[^/>]*\/>/gi, '')
    .trim();
  return { content, contextWarning };
}

/**
 * Parse <system-error> tags from messages (injected by backend for task/tag validation errors)
 * Format: <system-error type="task-update" code="E400">Error message</system-error>
 */
function parseSystemErrors(text) {
  if (!text || typeof text !== 'string') return { content: text || '', systemErrors: [] };

  const systemErrors = [];
  // Match <system-error type="..." code="...">message</system-error>
  const errorRegex = /<system-error([^>]*)>([\s\S]*?)<\/system-error>/gi;
  let match;
  while ((match = errorRegex.exec(text)) !== null) {
    const attrs = match[1];
    const message = match[2].trim();

    // Extract type and code attributes
    const typeMatch = /type="([^"]*)"/.exec(attrs);
    const codeMatch = /code="([^"]*)"/.exec(attrs);

    systemErrors.push({
      type: typeMatch ? typeMatch[1] : 'unknown',
      code: codeMatch ? codeMatch[1] : 'E500',
      message
    });
  }

  // Remove all system-error tags from content
  const content = text
    .replace(/<system-error[^>]*>[\s\S]*?<\/system-error>/gi, '')
    .trim();
  return { content, systemErrors };
}

/**
 * Parse <credentials-list> and <credentials-get> tags from messages
 * These are injected by backend when agent accesses credentials
 * Format: <credentials-list success="true" count="3"/>
 *         <credentials-get name="github-token" success="true"/>
 *         <credentials-get name="missing" success="false" error="Not found"/>
 */
function parseCredentialsTags(text) {
  if (!text || typeof text !== 'string') return { content: text || '', credentialOps: [] };

  const credentialOps = [];
  let match;


  // Match <credentials-list .../> (self-closing request)
  // Only show if it has explicit success/error attribute (skip pending requests)
  const listRegex = /<credentials-list([^>]*?)\/>/gi;
  while ((match = listRegex.exec(text)) !== null) {
    const attrs = match[1];
    const successMatch = /success="([^"]*)"/.exec(attrs);
    const errorMatch = /error="([^"]*)"/.exec(attrs);

    // Skip request tags without explicit status - they're just pending requests
    if (!successMatch && !errorMatch) continue;

    const countMatch = /count="([^"]*)"/.exec(attrs);
    const scopeMatch = /scope="([^"]*)"/.exec(attrs);

    credentialOps.push({
      type: 'list',
      success: successMatch ? successMatch[1] === 'true' : false,
      count: countMatch ? parseInt(countMatch[1], 10) : 0,
      scope: scopeMatch ? scopeMatch[1] : null,
      error: errorMatch ? errorMatch[1] : null
    });
  }

  // Match <credentials-list-response ...>...</credentials-list-response> OR <credentials-list-response .../> (self-closing)
  // Both formats indicate success
  const listResponseRegex = /<credentials-list-response([^>]*?)(?:\/>|>([\s\S]*?)<\/credentials-list-response>)/gi;
  while ((match = listResponseRegex.exec(text)) !== null) {
    const attrs = match[1];
    const content = match[2] || '';
    const scopeMatch = /scope="([^"]*)"/.exec(attrs);

    // Parse <credential .../> child elements in the response content
    // First try count attribute (if backend provides it), otherwise count and extract children
    const countAttrMatch = /count="([^"]*)"/.exec(attrs);
    let count = 0;
    const credentials = []; // Array of { name, type } objects for expand view

    // HTML decode content before parsing - backend may HTML-escape the credential tags
    const decodedContent = content
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&');

    if (countAttrMatch) {
      count = parseInt(countAttrMatch[1], 10);
    }

    // Extract credential details from child elements
    if (decodedContent) {
      const credentialRegex = /<credential\s+([^>]*)\/?>/gi;
      let credMatch;
      while ((credMatch = credentialRegex.exec(decodedContent)) !== null) {
        const credAttrs = credMatch[1];
        const nameMatch = /name="([^"]*)"/.exec(credAttrs);
        const typeMatch = /type="([^"]*)"/.exec(credAttrs);
        if (nameMatch) {
          credentials.push({
            name: nameMatch[1],
            type: typeMatch ? typeMatch[1] : null
          });
        }
      }
      // If count wasn't in attributes, use parsed count
      if (!countAttrMatch) {
        count = credentials.length;
      }
    }

    credentialOps.push({
      type: 'list',
      success: true, // Response means success
      count,
      credentials, // Include parsed credential details for expand view
      scope: scopeMatch ? scopeMatch[1] : null,
      error: null
    });
  }

  // Match <credentials-get .../> (self-closing request)
  // Only show if it has explicit success/error attribute (skip pending requests)
  const getRegex = /<credentials-get([^>]*?)\/>/gi;
  while ((match = getRegex.exec(text)) !== null) {
    const attrs = match[1];
    const successMatch = /success="([^"]*)"/.exec(attrs);
    const errorMatch = /error="([^"]*)"/.exec(attrs);

    // Skip request tags without explicit status - they're just pending requests
    if (!successMatch && !errorMatch) continue;

    const nameMatch = /name="([^"]*)"/.exec(attrs);
    const typeMatch = /type="([^"]*)"/.exec(attrs);

    credentialOps.push({
      type: 'get',
      name: nameMatch ? nameMatch[1] : 'unknown',
      credentialType: typeMatch ? typeMatch[1] : null,
      success: successMatch ? successMatch[1] === 'true' : false,
      error: errorMatch ? errorMatch[1] : null
    });
  }

  // Match <credentials-get-response ...>...</credentials-get-response> (response with encrypted content)
  const getResponseRegex = /<credentials-get-response([^>]*)>([\s\S]*?)<\/credentials-get-response>/gi;
  while ((match = getResponseRegex.exec(text)) !== null) {
    const attrs = match[1];
    const nameMatch = /name="([^"]*)"/.exec(attrs);
    const typeMatch = /type="([^"]*)"/.exec(attrs);
    const encryptedMatch = /encrypted="([^"]*)"/.exec(attrs);

    credentialOps.push({
      type: 'get',
      name: nameMatch ? nameMatch[1] : 'unknown',
      credentialType: typeMatch ? typeMatch[1] : null,
      success: true, // Response means success
      encrypted: encryptedMatch ? encryptedMatch[1] === 'true' : false,
      error: null
    });
  }

  // Match <credentials-error ...>...</credentials-error> (generic error response)
  const errorRegex = /<credentials-error([^>]*)>([\s\S]*?)<\/credentials-error>/gi;
  while ((match = errorRegex.exec(text)) !== null) {
    const attrs = match[1];
    const nameMatch = /name="([^"]*)"/.exec(attrs);
    const opMatch = /operation="([^"]*)"/.exec(attrs);
    const errorMessage = match[2].trim();

    credentialOps.push({
      type: opMatch ? opMatch[1] : 'get',
      name: nameMatch ? nameMatch[1] : 'unknown',
      success: false,
      error: errorMessage || 'Unknown error'
    });
  }

  // Match <credentials-get-error ...>...</credentials-get-error> (get-specific error)
  const getErrorRegex = /<credentials-get-error([^>]*)>([\s\S]*?)<\/credentials-get-error>/gi;
  while ((match = getErrorRegex.exec(text)) !== null) {
    const attrs = match[1];
    const nameMatch = /name="([^"]*)"/.exec(attrs);
    const codeMatch = /code="([^"]*)"/.exec(attrs);
    const errorMessage = match[2].trim();

    // Try to extract name from error message if not in attributes
    // Backend sends: "Credential 'test4' not found" - extract 'test4'
    let credName = nameMatch ? nameMatch[1] : null;
    if (!credName && errorMessage) {
      const contentNameMatch = /['"]([^'"]+)['"]/i.exec(errorMessage);
      if (contentNameMatch) {
        credName = contentNameMatch[1];
      }
    }

    credentialOps.push({
      type: 'get',
      name: credName || 'unknown',
      success: false,
      errorCode: codeMatch ? codeMatch[1] : null,
      error: errorMessage || 'Unknown error'
    });
  }

  // Match <credentials-list-error ...>...</credentials-list-error> (list-specific error)
  const listErrorRegex = /<credentials-list-error([^>]*)>([\s\S]*?)<\/credentials-list-error>/gi;
  while ((match = listErrorRegex.exec(text)) !== null) {
    const attrs = match[1];
    const codeMatch = /code="([^"]*)"/.exec(attrs);
    const errorMessage = match[2].trim();

    credentialOps.push({
      type: 'list',
      success: false,
      errorCode: codeMatch ? codeMatch[1] : null,
      error: errorMessage || 'Unknown error'
    });
  }

  // Remove all credentials tags from content
  const content = text
    .replace(/<credentials-list[^>]*\/>/gi, '')
    .replace(/<credentials-list-response[^>]*\/?>([\s\S]*?<\/credentials-list-response>)?/gi, '')
    .replace(/<credentials-list-error[^>]*>[\s\S]*?<\/credentials-list-error>/gi, '')
    .replace(/<credentials-get[^>]*\/>/gi, '')
    .replace(/<credentials-get-response[^>]*>[\s\S]*?<\/credentials-get-response>/gi, '')
    .replace(/<credentials-get-error[^>]*>[\s\S]*?<\/credentials-get-error>/gi, '')
    .replace(/<credentials-error[^>]*>[\s\S]*?<\/credentials-error>/gi, '')
    .trim();

  return { content, credentialOps };
}

/**
 * Parse and strip system feature request tags (<help/>, <query .../>) from messages
 * These are the tags the agent uses to request help/query - they should be hidden from UI
 * IMPORTANT: Only strip tags that are NOT inside code blocks (backticks)
 */
function parseSystemRequestTags(text) {
  if (!text || typeof text !== 'string') return { content: text || '', hasHelpRequest: false, hasQueryRequest: false };

  let content = text;
  let hasHelpRequest = false;
  let hasQueryRequest = false;

  // Temporarily replace code blocks to protect them from tag stripping
  const codeBlocks = [];
  // Triple backticks (code blocks)
  content = content.replace(/```[\s\S]*?```/g, (match) => {
    codeBlocks.push(match);
    return `__CODE_BLOCK_${codeBlocks.length - 1}__`;
  });
  // Single/double backticks (inline code)
  content = content.replace(/`[^`]+`/g, (match) => {
    codeBlocks.push(match);
    return `__CODE_BLOCK_${codeBlocks.length - 1}__`;
  });

  // Strip <help/> tags (self-closing) - only standalone tags, not inside text
  // Must be at start of line, end of line, or surrounded by whitespace/newlines
  const helpTagRegex = /(?:^|\s)(<help\s*\/?>)(?:\s|$)/gi;
  if (helpTagRegex.test(content)) {
    hasHelpRequest = true;
    content = content.replace(/(?:^|\s)<help\s*\/?>(?:\s|$)/gi, ' ').trim();
  }

  // Strip <query .../> tags (self-closing with attributes)
  const queryTagRegex = /(?:^|\s)(<query\s+[^>]*\/?>)(?:\s|$)/gi;
  if (queryTagRegex.test(content)) {
    hasQueryRequest = true;
    content = content.replace(/(?:^|\s)<query\s+[^>]*\/?>(?:\s|$)/gi, ' ').trim();
  }

  // Restore code blocks
  codeBlocks.forEach((block, idx) => {
    content = content.replace(`__CODE_BLOCK_${idx}__`, block);
  });

  return { content, hasHelpRequest, hasQueryRequest };
}

/**
 * Parse <help-response> tags from messages
 */

function parseHelpResponse(text) {
  if (!text || typeof text !== 'string') return { content: text || '', helpResponse: null };

  const helpResponseRegex = /<help-response>([\s\S]*?)<\/help-response>/i;
  const match = helpResponseRegex.exec(text);

  // DEBUG
  if (text && text.includes('help-response')) {
    console.log('[parseHelpResponse] Found help-response in text, match:', !!match);
  }

  if (!match) return { content: text, helpResponse: null };

  const innerContent = match[1];

  // Parse agent info
  const agentMatch = /<your-agent\s+uuid="([^"]*)"(?:\s+name="([^"]*)")?\s*\/>/.exec(innerContent);

  // Parse commands
  const commands = [];
  const commandRegex = /<command\s+name="([^"]*)">([\s\S]*?)<\/command>/gi;
  let commandMatch;
  while ((commandMatch = commandRegex.exec(innerContent)) !== null) {
    const cmdContent = commandMatch[2];
    const descMatch = /<description>([^<]*)<\/description>/.exec(cmdContent);
    const syntaxMatch = /<syntax><!\[CDATA\[([\s\S]*?)\]\]><\/syntax>/.exec(cmdContent);
    const exampleMatch = /<example><!\[CDATA\[([\s\S]*?)\]\]><\/example>/.exec(cmdContent);
    const noteMatch = /<note>([^<]*)<\/note>/.exec(cmdContent);

    // Parse modes if present
    const modes = [];
    const modeRegex = /<mode\s+name="([^"]*)">([^<]*)<\/mode>/gi;
    let modeMatch;
    while ((modeMatch = modeRegex.exec(cmdContent)) !== null) {
      modes.push({ name: modeMatch[1], description: modeMatch[2] });
    }

    // Parse types if present
    const types = [];
    const typeRegex = /<type\s+name="([^"]*)">([^<]*)<\/type>/gi;
    let typeMatch;
    while ((typeMatch = typeRegex.exec(cmdContent)) !== null) {
      types.push({ name: typeMatch[1], description: typeMatch[2] });
    }

    commands.push({
      name: commandMatch[1],
      description: descMatch ? descMatch[1] : null,
      syntax: syntaxMatch ? syntaxMatch[1] : null,
      example: exampleMatch ? exampleMatch[1] : null,
      note: noteMatch ? noteMatch[1] : null,
      modes: modes.length > 0 ? modes : null,
      types: types.length > 0 ? types : null
    });
  }

  const helpResponse = {
    agent: agentMatch ? { uuid: agentMatch[1], name: agentMatch[2] } : null,
    commands
  };

  const content = text.replace(/<help-response>[\s\S]*?<\/help-response>/gi, '').trim();
  return { content, helpResponse };
}

/**
 * Parse <query-response> tags from messages
 */
function parseQueryResponse(text) {
  if (!text || typeof text !== 'string') return { content: text || '', queryResponses: [] };

  const queryResponses = [];
  const queryResponseRegex = /<query-response\s+type="([^"]*)"(?:\s+channel="([^"]*)")?>([\s\S]*?)<\/query-response>/gi;
  let match;

  while ((match = queryResponseRegex.exec(text)) !== null) {
    const [fullMatch, type, channel, innerContent] = match;

    if (type === 'channels') {
      const channels = [];
      const channelRegex = /<channel\s+id="([^"]*)"(?:\s+name="([^"]*)")?(?:\s+display-name="([^"]*)")?(?:\s+member-count="(\d+)")?\s*\/?>/gi;
      let channelMatch;
      while ((channelMatch = channelRegex.exec(innerContent)) !== null) {
        channels.push({
          id: channelMatch[1],
          name: channelMatch[2],
          displayName: channelMatch[3],
          memberCount: channelMatch[4] ? parseInt(channelMatch[4], 10) : null
        });
      }
      queryResponses.push({ type: 'channels', channels });
    } else if (type === 'members') {
      const members = [];
      const memberRegex = /<member\s+type="([^"]*)"(?:\s+id="([^"]*)")?(?:\s+name="([^"]*)")?(?:\s+status="([^"]*)")?\s*\/?>/gi;
      let memberMatch;
      while ((memberMatch = memberRegex.exec(innerContent)) !== null) {
        members.push({
          type: memberMatch[1],
          id: memberMatch[2],
          name: memberMatch[3],
          status: memberMatch[4]
        });
      }
      queryResponses.push({ type: 'members', channel, members });
    } else if (type === 'status') {
      const agentMatch = /<agent\s+uuid="([^"]*)"(?:\s+name="([^"]*)")?\s*\/>/.exec(innerContent);
      const statusMatch = /<status\s+mode="([^"]*)"(?:\s+project="([^"]*)")?\s*\/>/.exec(innerContent);
      const queueMatch = /<queue\s+pending="(\d+)"\s*\/>/.exec(innerContent);
      queryResponses.push({
        type: 'status',
        agent: agentMatch ? { uuid: agentMatch[1], name: agentMatch[2] } : null,
        status: statusMatch ? { mode: statusMatch[1], project: statusMatch[2] } : null,
        queue: queueMatch ? { pending: parseInt(queueMatch[1], 10) } : null
      });
    } else if (type === 'queue') {
      const summaryMatch = /<summary\s+pending="(\d+)"(?:\s+oldest="([^"]*)")?\s*\/>/.exec(innerContent);
      const byChannel = [];
      const channelRegex = /<channel\s+id="([^"]*)"(?:\s+name="([^"]*)")?(?:\s+count="(\d+)")?\s*\/?>/gi;
      let channelMatch;
      while ((channelMatch = channelRegex.exec(innerContent)) !== null) {
        byChannel.push({
          id: channelMatch[1],
          name: channelMatch[2],
          count: channelMatch[3] ? parseInt(channelMatch[3], 10) : 0
        });
      }
      queryResponses.push({
        type: 'queue',
        summary: summaryMatch ? { pending: parseInt(summaryMatch[1], 10), oldest: summaryMatch[2] } : null,
        byChannel
      });
    }
  }

  const content = text.replace(/<query-response\s+type="[^"]*"(?:\s+channel="[^"]*)?>[\s\S]*?<\/query-response>/gi, '').trim();
  return { content, queryResponses };
}

/**
 * Parse task response tags from system_response content
 * Returns parsed task tags in the format expected by TaskActivityIndicator
 */
function parseTaskResponse(text) {
  if (!text || typeof text !== 'string') return { content: text || '', taskTags: [] };

  const taskTags = [];

  // Task response tag patterns (self-closing tags with attributes)
  const taskTagPatterns = [
    { regex: /<task-created\s+([^/>]*)\/>/gi, tagType: 'task-created' },
    { regex: /<task-updated\s+([^/>]*)\/>/gi, tagType: 'task-updated' },
    { regex: /<task-deleted\s+([^/>]*)\/>/gi, tagType: 'task-deleted' },
    { regex: /<task-assigned\s+([^/>]*)\/>/gi, tagType: 'task-assigned' },
    { regex: /<task-unassigned\s+([^/>]*)\/>/gi, tagType: 'task-unassigned' },
    { regex: /<task-labels-updated\s+([^/>]*)\/>/gi, tagType: 'task-labels-updated' },
    { regex: /<task-comment-added\s+([^/>]*)\/>/gi, tagType: 'task-comment-added' },
  ];

  // Task error tag (with content)
  const errorRegex = /<task-error>([^<]*)<\/task-error>/gi;
  let errorMatch;
  while ((errorMatch = errorRegex.exec(text)) !== null) {
    taskTags.push({
      tagType: 'task-error',
      attributes: {},
      content: errorMatch[1]
    });
  }

  // Parse self-closing tags
  for (const { regex, tagType } of taskTagPatterns) {
    regex.lastIndex = 0; // Reset regex state
    let match;
    while ((match = regex.exec(text)) !== null) {
      const attrsString = match[1];
      const attributes = {};

      // Parse attributes from the tag
      const attrRegex = /(\w+(?:-\w+)*)="([^"]*)"/g;
      let attrMatch;
      while ((attrMatch = attrRegex.exec(attrsString)) !== null) {
        // Convert kebab-case to camelCase for attribute names
        const attrName = attrMatch[1].replace(/-([a-z])/g, (_, c) => c.toUpperCase());
        attributes[attrMatch[1]] = attrMatch[2]; // Keep original name too
        attributes[attrName] = attrMatch[2];
      }

      taskTags.push({
        tagType,
        attributes,
        content: null
      });
    }
  }

  // Remove task tags from content
  let cleanContent = text;
  for (const { regex } of taskTagPatterns) {
    regex.lastIndex = 0;
    cleanContent = cleanContent.replace(regex, '');
  }
  cleanContent = cleanContent.replace(/<task-error>[^<]*<\/task-error>/gi, '').trim();

  return { content: cleanContent, taskTags };
}

/**
 * Parse <chat-message> tags from user messages (incoming chat messages to Claude)
 * These are formatted by the backend when chat messages are received
 * Returns the actual message content and metadata
 */
function parseChatMessageTags(text) {
  if (!text || typeof text !== 'string') return { content: text || '', chatMessages: [], hasOnlyChatMessages: false };

  const chatMessages = [];
  // Match <chat-message uuid="..." timestamp="...">...</chat-message>
  const chatMessageRegex = /<chat-message\s+([^>]*)>([\s\S]*?)<\/chat-message>/gi;
  let match;

  while ((match = chatMessageRegex.exec(text)) !== null) {
    const attrs = match[1];
    const innerContent = match[2];

    // Parse attributes
    const uuidMatch = /uuid="([^"]*)"/.exec(attrs);
    const timestampMatch = /timestamp="([^"]*)"/.exec(attrs);

    // Parse inner elements
    const fromMatch = /<from\s+type="([^"]*)"\s+id="([^"]*)"\s+name="([^"]*)"/.exec(innerContent);
    const channelMatch = /<channel\s+id="([^"]*)"\s+name="([^"]*)"/.exec(innerContent);
    const contentMatch = /<content>([\s\S]*?)<\/content>/.exec(innerContent);

    chatMessages.push({
      fullMatch: match[0],
      uuid: uuidMatch ? uuidMatch[1] : null,
      timestamp: timestampMatch ? timestampMatch[1] : null,
      from: fromMatch ? { type: fromMatch[1], id: fromMatch[2], name: fromMatch[3] } : null,
      channel: channelMatch ? { id: channelMatch[1], name: channelMatch[2] } : null,
      content: contentMatch ? contentMatch[1].trim() : ''
    });
  }

  // Remove chat-message tags from content
  const cleanContent = text.replace(/<chat-message\s+[^>]*>[\s\S]*?<\/chat-message>/gi, '').trim();
  // Check if the message only contains chat-message tags (no other content)
  const hasOnlyChatMessages = chatMessages.length > 0 && !cleanContent;

  return { content: cleanContent, chatMessages, hasOnlyChatMessages };
}

/**
 * Parse a single <chat> tag using safeParseXml
 * Returns parsed attributes and content, or null if invalid
 */
function parseSingleChatTag(xmlString) {
  try {
    const chatEl = safeParseXml(xmlString, 'chat');
    if (!chatEl) return null;

    return {
      fullMatch: xmlString,
      content: chatEl.textContent?.trim() || '',
      channel: chatEl.getAttribute('channel'),
      dmTo: chatEl.getAttribute('dm-to'),
      agent: chatEl.getAttribute('agent'),
      isDm: chatEl.hasAttribute('dm-to')
    };
  } catch {
    return null;
  }
}

/**
 * Parse <chat> tags from Claude output for inline badge rendering
 * Supports both channel messages and DMs
 * Handles both <chat attr="...">content</chat> and simplified <chat>content</chat>
 * Uses DOMParser for robust XML parsing (no regex for validation)
 */
function parseChatTags(text) {
  if (!text || typeof text !== 'string') return { content: text || '', chatTags: [] };

  const chatTags = [];

  // Use regex only to FIND potential tags, not to validate/parse them
  // DOMParser handles the actual parsing and validation
  const tagFinder = /<chat(?:\s[^>]*)?>[\s\S]*?<\/chat>/gi;
  let match;

  while ((match = tagFinder.exec(text)) !== null) {
    const parsed = parseSingleChatTag(match[0]);
    if (parsed) {
      chatTags.push(parsed);
    }
  }

  // Remove successfully parsed chat tags from content
  let cleanContent = text;
  for (const tag of chatTags) {
    cleanContent = cleanContent.replace(tag.fullMatch, '');
  }

  return { content: cleanContent.trim(), chatTags };
}

/**
 * Parse <batched-messages> tags from user input (incoming chat messages)
 * Format:
 * <batched-messages count="N">
 *   <message from="SenderName" channel="channelName" time="ISO" mention="true/false">
 *     Message content
 *   </message>
 * </batched-messages>
 * Returns parsed batched messages and cleaned content
 */
function parseBatchedMessagesTags(text) {
  if (!text || typeof text !== 'string') return { content: text || '', batchedMessages: [] };

  const batchedMessages = [];
  const tagFinder = /<batched-messages(?:\s[^>]*)?>[\s\S]*?<\/batched-messages>/gi;
  let match;

  while ((match = tagFinder.exec(text)) !== null) {
    const fullMatch = match[0];
    try {
      const batchedEl = safeParseXml(fullMatch, 'batched-messages');
      if (!batchedEl) continue;

      const count = parseInt(batchedEl.getAttribute('count') || '0', 10);
      const messageEls = batchedEl.querySelectorAll('message');
      const messages = [];

      for (const msgEl of messageEls) {
        messages.push({
          from: msgEl.getAttribute('from') || 'Unknown',
          channel: msgEl.getAttribute('channel') || '',
          time: msgEl.getAttribute('time') || '',
          mention: msgEl.getAttribute('mention') === 'true',
          content: msgEl.textContent?.trim() || ''
        });
      }

      batchedMessages.push({
        fullMatch,
        count,
        messages
      });
    } catch {
      // Skip malformed tags
    }
  }

  // Remove successfully parsed batched-messages tags from content
  let cleanContent = text;
  for (const batch of batchedMessages) {
    cleanContent = cleanContent.replace(batch.fullMatch, '');
  }

  return { content: cleanContent.trim(), batchedMessages };
}

/**
 * Parse a single task tag using safeParseXml
 * Supports: task-create, task-update, task-assign, task-unassign, task-labels, task-comment
 * Returns parsed attributes and tag type, or null if invalid
 */
function parseSingleTaskTag(xmlString, tagName) {
  try {
    const tagEl = safeParseXml(xmlString, tagName);
    if (!tagEl) return null;

    // Extract all attributes as an object
    const attributes = {};
    for (const attr of tagEl.attributes) {
      attributes[attr.name] = attr.value;
    }

    // Get text content (for task-comment)
    const textContent = tagEl.textContent?.trim() || '';

    return {
      fullMatch: xmlString,
      tagType: tagName,
      attributes,
      content: textContent
    };
  } catch {
    return null;
  }
}

/**
 * Parse task-related tags from Claude output for inline rendering
 * Supports: task-create, task-update, task-assign, task-unassign, task-labels, task-comment
 * Also parses response tags: task-created, task-updated, task-assigned, etc.
 * Uses DOMParser for robust XML parsing
 */
function parseTaskTags(text) {
  if (!text || typeof text !== 'string') return { content: text || '', taskTags: [] };

  const taskTags = [];

  // All task tag types (request and response)
  const taskTagTypes = [
    'task-create', 'task-update', 'task-delete', 'task-assign', 'task-unassign', 'task-labels', 'task-comment',
    'task-created', 'task-updated', 'task-deleted', 'task-assigned', 'task-unassigned', 'task-labels-updated', 'task-comment-added',
    'task-error'
  ];

  for (const tagName of taskTagTypes) {
    // Use regex only to FIND potential tags
    // Self-closing tags
    const selfClosingFinder = new RegExp(`<${tagName}\\s+[^/>]*/>`, 'gi');
    // Content tags (for task-comment, task-error)
    const contentFinder = new RegExp(`<${tagName}(?:\\s[^>]*)?>([\\s\\S]*?)</${tagName}>`, 'gi');

    let match;
    while ((match = selfClosingFinder.exec(text)) !== null) {
      const parsed = parseSingleTaskTag(match[0], tagName);
      if (parsed) {
        taskTags.push(parsed);
      }
    }

    while ((match = contentFinder.exec(text)) !== null) {
      const parsed = parseSingleTaskTag(match[0], tagName);
      if (parsed) {
        taskTags.push(parsed);
      }
    }
  }

  // Remove successfully parsed task tags from content
  let cleanContent = text;
  for (const tag of taskTags) {
    cleanContent = cleanContent.replace(tag.fullMatch, '');
  }

  return { content: cleanContent.trim(), taskTags };
}

/**
 * Parse a single insight tag
 * @param {string} xmlString - The full XML tag string
 * @param {string} tagName - The tag name (insight, insight-query, insight-result)
 * @returns {object|null} Parsed tag data or null if invalid
 */
function parseSingleInsightTag(xmlString, tagName) {
  try {
    const tagEl = safeParseXml(xmlString, tagName);
    if (!tagEl) return null;

    // Extract all attributes as an object
    const attributes = {};
    for (const attr of tagEl.attributes) {
      attributes[attr.name] = attr.value;
    }

    // Get text content (direct text, not from children)
    const textContent = tagEl.textContent?.trim() || '';

    // For insight-result, extract child insight elements
    let children = [];
    if (tagName === 'insight-result') {
      const childInsights = tagEl.querySelectorAll('insight');
      children = Array.from(childInsights).map(child => {
        const childAttrs = {};
        for (const attr of child.attributes) {
          childAttrs[attr.name] = attr.value;
        }
        return {
          tagType: 'insight',
          attributes: childAttrs,
          content: child.textContent?.trim() || ''
        };
      });
    }

    return {
      fullMatch: xmlString,
      tagType: tagName,
      attributes,
      content: textContent,
      children: children.length > 0 ? children : undefined
    };
  } catch {
    return null;
  }
}

/**
 * Parse insight-related tags from Claude output for inline rendering
 * Supports: insight, insight-query, insight-result
 * Uses DOMParser for robust XML parsing
 * @param {string} text - The text to parse
 * @returns {object} { content: cleanedContent, insightTags: [...] }
 */
function parseInsightTags(text) {
  if (!text || typeof text !== 'string') return { content: text || '', insightTags: [] };

  const insightTags = [];
  let cleanContent = text;

  // First, parse insight-result tags (these contain child insight elements)
  const insightResultFinder = /<insight-result(?:\s[^>]*)?>[\s\S]*?<\/insight-result>/gi;
  let match;
  while ((match = insightResultFinder.exec(text)) !== null) {
    const parsed = parseSingleInsightTag(match[0], 'insight-result');
    if (parsed) {
      insightTags.push(parsed);
      cleanContent = cleanContent.replace(match[0], '');
    }
  }

  // Then parse insight-query tags
  const insightQueryFinder = /<insight-query(?:\s[^>]*)?>[\s\S]*?<\/insight-query>/gi;
  while ((match = insightQueryFinder.exec(text)) !== null) {
    const parsed = parseSingleInsightTag(match[0], 'insight-query');
    if (parsed) {
      insightTags.push(parsed);
      cleanContent = cleanContent.replace(match[0], '');
    }
  }

  // Finally, parse standalone insight tags (not inside insight-result)
  const insightFinder = /<insight(?:\s[^>]*)?>[\s\S]*?<\/insight>/gi;
  const insightSelfClosingFinder = /<insight\s+[^/>]*\/>/gi;
  while ((match = insightFinder.exec(cleanContent)) !== null) {
    const parsed = parseSingleInsightTag(match[0], 'insight');
    if (parsed) {
      insightTags.push(parsed);
    }
  }
  while ((match = insightSelfClosingFinder.exec(cleanContent)) !== null) {
    const parsed = parseSingleInsightTag(match[0], 'insight');
    if (parsed) {
      insightTags.push(parsed);
    }
  }

  // Remove all insight tags from content
  cleanContent = cleanContent
    .replace(/<insight-result(?:\s[^>]*)?>[\s\S]*?<\/insight-result>/gi, '')
    .replace(/<insight-query(?:\s[^>]*)?>[\s\S]*?<\/insight-query>/gi, '')
    .replace(/<insight(?:\s[^>]*)?>[\s\S]*?<\/insight>/gi, '')
    .replace(/<insight\s+[^/>]*\/>/gi, '')
    .trim();

  return { content: cleanContent, insightTags };
}

// Cache for channel names (UUID -> name mapping)
const channelNameCache = new Map();
let channelCacheLoaded = false;
let defaultChannelName = 'project';

/**
 * Load channel names into cache
 */
async function loadChannelNames() {
  if (channelCacheLoaded) return;
  try {
    console.log('[MessageList] Loading channel names...');
    const response = await fetch('/api/channels');
    console.log('[MessageList] Channels API response:', response.status);
    if (response.ok) {
      const result = await response.json();
      // API returns { data: [...], pagination: {...} }
      const channels = result.data || result;
      console.log('[MessageList] Loaded channels:', channels.length, channels.map(c => ({ id: c.id, name: c.name })));
      channels.forEach(ch => channelNameCache.set(ch.id, ch.name));
      // First channel is typically the project's default channel
      if (channels.length > 0) {
        defaultChannelName = channels[0].name;
      }
      channelCacheLoaded = true;
    }
  } catch (err) {
    console.log('[MessageList] Failed to load channels:', err.message);
    // Silently fail - we'll just show UUIDs
  }
}

/**
 * Get channel name from UUID, or return default channel name if not specified
 */
function getChannelName(channelId) {
  if (!channelId) return defaultChannelName;
  const cached = channelNameCache.get(channelId);
  if (cached) return cached;
  // Return shortened UUID as fallback
  return channelId.substring(0, 8);
}

/**
 * Standalone chat activity indicator for message flow
 * Renders like system messages - no avatar, no bubble, just inline in the flow
 */
function ChatActivityIndicator({ chatTags }) {
  const [expanded, setExpanded] = useState(false);
  const [, forceUpdate] = useState(0);

  // Load channel names on mount
  useEffect(() => {
    if (!channelCacheLoaded) {
      loadChannelNames().then(() => forceUpdate(n => n + 1));
    }
  }, []);

  if (!chatTags || chatTags.length === 0) return null;

  const dmCount = chatTags.filter(t => t.isDm).length;
  const channelTags = chatTags.filter(t => !t.isDm);

  // Build summary text with channel names
  let summaryParts = [];
  if (dmCount > 0) summaryParts.push(`${dmCount} DM${dmCount > 1 ? 's' : ''}`);
  if (channelTags.length > 0) {
    // Get unique channel names
    const uniqueChannels = [...new Set(channelTags.map(t => getChannelName(t.channel)))];
    if (uniqueChannels.length === 1) {
      summaryParts.push(`message in #${uniqueChannels[0]}`);
    } else {
      summaryParts.push(`${channelTags.length} messages`);
    }
  }
  const summaryText = `Processing ${summaryParts.join(', ')}...`;

  return (
    <div className="flex flex-col items-center gap-1 py-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
      >
        <Info className="w-3.5 h-3.5" />
        <span>{summaryText}</span>
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>
      {expanded && (
        <div className="mt-1 p-3 bg-emerald-900/10 border border-emerald-500/20 rounded-lg space-y-2 max-w-lg">
          {chatTags.map((tag, idx) => {
            const preview = tag.content.length > 100
              ? tag.content.substring(0, 100) + '...'
              : tag.content;
            return (
              <div key={idx} className="flex items-start gap-2 text-sm">
                {tag.isDm ? (
                  <Send className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                ) : (
                  <MessageCircle className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                )}
                <div>
                  <span className="text-emerald-400 font-medium">
                    {tag.isDm ? 'DM' : `#${getChannelName(tag.channel)}`}:
                  </span>
                  <span className="text-gray-300 ml-1">{preview}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Batched messages indicator - shows when messages were batched during agent generation
 * Expandable to show individual message previews
 */
function BatchedMessagesIndicator({ batchedMessages }) {
  const [expanded, setExpanded] = useState(false);

  if (!batchedMessages || batchedMessages.length === 0) return null;

  // Calculate total count across all batches
  const totalCount = batchedMessages.reduce((acc, batch) => acc + batch.count, 0);
  const allMessages = batchedMessages.flatMap(batch => batch.messages);

  // Build summary text
  const uniqueChannels = [...new Set(allMessages.map(m => m.channel).filter(Boolean))];
  const uniqueSenders = [...new Set(allMessages.map(m => m.from))];

  let summaryText = `${totalCount} batched message${totalCount > 1 ? 's' : ''}`;
  if (uniqueChannels.length === 1) {
    summaryText += ` in #${uniqueChannels[0]}`;
  } else if (uniqueChannels.length > 1) {
    summaryText += ` across ${uniqueChannels.length} channels`;
  }
  if (uniqueSenders.length === 1) {
    summaryText += ` from ${uniqueSenders[0]}`;
  }

  return (
    <div className="flex flex-col items-center gap-1 py-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-xs text-amber-400 hover:text-amber-300 transition-colors"
      >
        <Inbox className="w-3.5 h-3.5" />
        <span>{summaryText}</span>
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>
      {expanded && allMessages.length > 0 && (
        <div className="mt-1 p-3 bg-amber-900/10 border border-amber-500/20 rounded-lg space-y-2 max-w-lg">
          {allMessages.map((msg, idx) => {
            const preview = msg.content.length > 100
              ? msg.content.substring(0, 100) + '...'
              : msg.content;
            const timeStr = msg.time ? new Date(msg.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
            return (
              <div key={idx} className="flex items-start gap-2 text-sm">
                <MessageCircle className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${msg.mention ? 'text-amber-400' : 'text-gray-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-amber-400 font-medium">{msg.from}</span>
                    {msg.channel && <span className="text-gray-500 text-xs">in #{msg.channel}</span>}
                    {timeStr && <span className="text-gray-600 text-xs">{timeStr}</span>}
                    {msg.mention && <span className="text-amber-500 text-xs bg-amber-500/10 px-1 rounded">@mentioned</span>}
                  </div>
                  <p className="text-gray-300 mt-0.5 break-words">{preview}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Credentials list indicator - shows credential list results with expandable details
 * Similar to BatchedMessagesIndicator but for credentials
 */
function CredentialsListIndicator({ details }) {
  const [expanded, setExpanded] = useState(false);

  if (!details) return null;

  const { success, count, credentials = [], scope, error, errorCode } = details;

  // Error state
  if (!success) {
    return (
      <div className="flex flex-col items-center gap-1 py-1">
        <div className="flex items-center gap-2 text-xs text-red-400">
          <Key className="w-3.5 h-3.5" />
          <span>Credentials list failed: {error || 'Unknown error'}</span>
        </div>
      </div>
    );
  }

  // Success state with expandable list
  const scopeText = scope ? ` (${scope})` : '';
  const summaryText = `Listed ${count} credential${count !== 1 ? 's' : ''}${scopeText}`;

  // If no credentials to show, render non-expandable
  if (!credentials || credentials.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-indigo-400 py-1">
        <Key className="w-3.5 h-3.5" />
        <span>{summaryText}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-1 py-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
      >
        <Key className="w-3.5 h-3.5" />
        <span>{summaryText}</span>
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>
      {expanded && (
        <div className="mt-1 p-3 bg-indigo-900/10 border border-indigo-500/20 rounded-lg space-y-1.5 max-w-lg">
          {credentials.map((cred, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm">
              <Key className="w-3 h-3 text-indigo-400 flex-shrink-0" />
              <span className="text-indigo-300 font-medium">{cred.name}</span>
              {cred.type && (
                <span className="text-gray-500 text-xs bg-gray-700/30 px-1.5 py-0.5 rounded">{cred.type}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Credentials get indicator - shows credential get result with expandable details
 * Similar to CredentialsListIndicator but for single credential retrieval
 */
function CredentialsGetIndicator({ details }) {
  const [expanded, setExpanded] = useState(false);

  if (!details) return null;

  const { success, name, credentialType, encrypted, error, errorCode } = details;

  // Error state
  if (!success) {
    // Check for server configuration errors (E500) vs credential-specific errors
    const isServerError = errorCode === 'E500' || error?.includes('not configured');
    const errorText = isServerError
      ? `Credentials error: ${error || 'Server configuration error'}`
      : `Credential not found: ${name || 'unknown'}`;

    return (
      <div className="flex flex-col items-center gap-1 py-1">
        <div className="flex items-center gap-2 text-xs text-red-400">
          <Key className="w-3.5 h-3.5" />
          <span>{errorText}</span>
        </div>
      </div>
    );
  }

  // Success state with expandable details
  const summaryText = `Loaded credential: ${name || 'unknown'}`;

  // Details to show in expand view
  const hasDetails = credentialType || encrypted !== undefined;

  // If no details to show, render non-expandable
  if (!hasDetails) {
    return (
      <div className="flex items-center gap-2 text-xs text-indigo-400 py-1">
        <Key className="w-3.5 h-3.5" />
        <span>{summaryText}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-1 py-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
      >
        <Key className="w-3.5 h-3.5" />
        <span>{summaryText}</span>
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>
      {expanded && (
        <div className="mt-1 p-3 bg-indigo-900/10 border border-indigo-500/20 rounded-lg space-y-1.5 max-w-lg">
          <div className="flex items-center gap-2 text-sm">
            <Key className="w-3 h-3 text-indigo-400 flex-shrink-0" />
            <span className="text-indigo-300 font-medium">{name}</span>
          </div>
          <div className="flex flex-wrap gap-2 ml-5">
            {credentialType && (
              <span className="text-gray-500 text-xs bg-gray-700/30 px-1.5 py-0.5 rounded">
                Type: {credentialType}
              </span>
            )}
            {encrypted !== undefined && (
              <span className={`text-xs px-1.5 py-0.5 rounded ${encrypted ? 'text-green-400 bg-green-900/30' : 'text-yellow-400 bg-yellow-900/30'}`}>
                {encrypted ? 'Encrypted' : 'Unencrypted'}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Task activity indicator - shows task operations in a card style like ToolUseCard
 * For task management XML tags (task-create, task-update, etc.)
 */
function TaskActivityIndicator({ taskTags }) {
  const [showDetails, setShowDetails] = useState(false);

  if (!taskTags || taskTags.length === 0) return null;

  // Task type configuration (similar to TOOL_CONFIG) - using cyan for all task operations
  const TASK_CONFIG = {
    'task-create': { icon: Plus, color: 'cyan', label: 'Creating task' },
    'task-update': { icon: Pencil, color: 'cyan', label: 'Updating task' },
    'task-delete': { icon: Trash2, color: 'red', label: 'Deleting task' },
    'task-assign': { icon: User, color: 'cyan', label: 'Assigning task' },
    'task-unassign': { icon: UserMinus, color: 'cyan', label: 'Unassigning task' },
    'task-labels': { icon: Tag, color: 'cyan', label: 'Updating labels' },
    'task-comment': { icon: MessageSquare, color: 'cyan', label: 'Adding comment' },
    'task-created': { icon: CheckCircle, color: 'green', label: 'Task created' },
    'task-updated': { icon: CheckCircle, color: 'green', label: 'Task updated' },
    'task-deleted': { icon: Trash2, color: 'red', label: 'Task deleted' },
    'task-assigned': { icon: CheckCircle, color: 'green', label: 'Task assigned' },
    'task-unassigned': { icon: CheckCircle, color: 'green', label: 'Task unassigned' },
    'task-labels-updated': { icon: CheckCircle, color: 'green', label: 'Labels updated' },
    'task-comment-added': { icon: CheckCircle, color: 'green', label: 'Comment added' },
    'task-error': { icon: AlertCircle, color: 'red', label: 'Task error' }
  };

  // Get summary for a task tag
  const getTaskSummary = (tag) => {
    const attrs = tag.attributes;
    switch (tag.tagType) {
      case 'task-create':
        return attrs.title ? `"${attrs.title}"` : 'new task';
      case 'task-update':
        return attrs.status ? `→ ${attrs.status}` : 'status update';
      case 'task-delete':
      case 'task-deleted':
        return attrs.id ? `#${attrs.id.substring(0, 8)}` : 'deleted';
      case 'task-assign':
      case 'task-assigned':
        return attrs.assignee || attrs['assignee-name'] || 'assignment';
      case 'task-unassign':
      case 'task-unassigned':
        return 'removed assignment';
      case 'task-labels':
      case 'task-labels-updated':
        return attrs.add || attrs.remove || 'labels';
      case 'task-comment':
      case 'task-comment-added':
        return tag.content ? `"${tag.content.substring(0, 30)}${tag.content.length > 30 ? '...' : ''}"` : 'comment';
      case 'task-created':
        return attrs.title ? `"${attrs.title}"` : (attrs.id ? `#${attrs.id.substring(0, 8)}` : 'success');
      case 'task-updated':
        return attrs.status || (attrs.id ? `#${attrs.id.substring(0, 8)}` : 'success');
      case 'task-error':
        return tag.content || 'error';
      default:
        return '';
    }
  };

  // Determine if this is a response tag (success/error)
  const isResponseTag = (tagType) => tagType.endsWith('-created') || tagType.endsWith('-updated') ||
    tagType.endsWith('-assigned') || tagType.endsWith('-unassigned') ||
    tagType.endsWith('-added') || tagType.endsWith('-deleted') || tagType === 'task-error';

  const firstTag = taskTags[0];
  const config = TASK_CONFIG[firstTag.tagType] || { icon: ClipboardList, color: 'gray', label: firstTag.tagType };
  const colors = COLOR_CLASSES[config.color] || COLOR_CLASSES.gray;
  const Icon = config.icon;
  const summary = getTaskSummary(firstTag);
  const isResponse = isResponseTag(firstTag.tagType);
  const isError = firstTag.tagType === 'task-error';

  return (
    <div className={`ml-11 inline-block max-w-[85%] rounded-lg border ${colors.border} ${colors.bg} overflow-hidden transition-all duration-200 shadow-lg ${colors.glow}`}>
      {/* Header */}
      <div className="flex items-center gap-3 p-2.5">
        <div className={`w-7 h-7 rounded-md ${colors.bg} border ${colors.border} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${colors.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium ${colors.text}`}>{config.label}</span>
            <span className="text-gray-600">-</span>
            <code className="text-xs text-gray-300 truncate">{summary}</code>
            {isResponse && !isError && (
              <>
                <span className="text-gray-600">-</span>
                <span className="inline-flex items-center gap-1 text-xs text-green-400">
                  <CheckCircle className="w-3 h-3" />
                  OK
                </span>
              </>
            )}
            {isError && (
              <>
                <span className="text-gray-600">-</span>
                <span className="inline-flex items-center gap-1 text-xs text-red-400">
                  <AlertCircle className="w-3 h-3" />
                  Error
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Details toggle */}
      <div className="border-t border-gray-700/50">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs bg-gray-900/30 hover:bg-gray-900/50 text-gray-400 transition-colors"
        >
          {showDetails ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <span>Details</span>
          <span className="text-gray-600">({Object.keys(firstTag.attributes).length} attributes)</span>
        </button>

        {showDetails && (
          <div className="p-3 bg-gray-900/50 max-h-48 overflow-y-auto">
            {taskTags.map((tag, idx) => (
              <div key={idx} className={`${idx > 0 ? 'mt-3 pt-3 border-t border-gray-700' : ''}`}>
                {taskTags.length > 1 && (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-gray-500">{TASK_CONFIG[tag.tagType]?.label || tag.tagType}</span>
                  </div>
                )}
                <div className="space-y-1">
                  {Object.entries(tag.attributes).map(([key, value]) => (
                    <div key={key} className="flex items-start gap-2 text-xs font-mono">
                      <span className="text-purple-400 flex-shrink-0">{key}:</span>
                      <span className="text-green-400 break-all">{value}</span>
                    </div>
                  ))}
                  {tag.content && (
                    <div className="mt-2 pt-2 border-t border-gray-700">
                      <span className="text-xs text-gray-500">Content:</span>
                      <pre className="text-xs text-gray-300 mt-1 whitespace-pre-wrap">{tag.content}</pre>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Insight activity indicator - shows when agent creates/queries insights
 * Styled like TaskActivityIndicator with purple base color and severity badges
 */
function InsightActivityIndicator({ insightTags }) {
  const [showDetails, setShowDetails] = useState(false);

  if (!insightTags || insightTags.length === 0) return null;

  // Insight type configuration
  const INSIGHT_CONFIG = {
    'insight': { icon: Lightbulb, color: 'purple', label: 'Creating insight' },
    'insight-query': { icon: Search, color: 'purple', label: 'Querying insights' },
    'insight-result': { icon: Database, color: 'green', label: 'Found insights' }
  };

  // Severity color mapping
  const SEVERITY_COLORS = {
    'high': { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
    'medium': { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
    'low': { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' }
  };

  // Get summary for an insight tag
  const getInsightSummary = (tag) => {
    const attrs = tag.attributes;
    switch (tag.tagType) {
      case 'insight':
        return attrs.type ? `[${attrs.type}]` : 'new insight';
      case 'insight-query':
        return tag.content ? `"${tag.content.substring(0, 40)}${tag.content.length > 40 ? '...' : ''}"` : 'query';
      case 'insight-result': {
        const count = attrs.count || (tag.children?.length || 0);
        return `${count} result${count !== 1 ? 's' : ''}`;
      }
      default:
        return '';
    }
  };

  const firstTag = insightTags[0];
  const config = INSIGHT_CONFIG[firstTag.tagType] || { icon: Lightbulb, color: 'purple', label: firstTag.tagType };
  const colors = COLOR_CLASSES[config.color] || COLOR_CLASSES.purple;
  const Icon = config.icon;
  const summary = getInsightSummary(firstTag);
  const severity = firstTag.attributes?.severity;
  const severityColors = severity ? SEVERITY_COLORS[severity] : null;

  // For insight-result, get children insights
  const childInsights = firstTag.tagType === 'insight-result' ? (firstTag.children || []) : [];

  return (
    <div className={`ml-11 inline-block max-w-[85%] rounded-lg border ${colors.border} ${colors.bg} overflow-hidden transition-all duration-200 shadow-lg ${colors.glow}`}>
      {/* Header */}
      <div className="flex items-center gap-3 p-2.5">
        <div className={`w-7 h-7 rounded-md ${colors.bg} border ${colors.border} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${colors.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-medium ${colors.text}`}>{config.label}</span>
            {severity && severityColors && (
              <span className={`text-xs px-1.5 py-0.5 rounded ${severityColors.bg} ${severityColors.text} border ${severityColors.border}`}>
                {severity}
              </span>
            )}
            <span className="text-gray-600">-</span>
            <code className="text-xs text-gray-300 truncate">{summary}</code>
            {firstTag.tagType === 'insight-result' && (
              <>
                <span className="text-gray-600">-</span>
                <span className="inline-flex items-center gap-1 text-xs text-green-400">
                  <CheckCircle className="w-3 h-3" />
                  OK
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* For insight-result: show child insights */}
      {firstTag.tagType === 'insight-result' && childInsights.length > 0 && (
        <div className="border-t border-gray-700/50">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs bg-gray-900/30 hover:bg-gray-900/50 text-gray-400 transition-colors"
          >
            {showDetails ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            <span>Show {childInsights.length} insight{childInsights.length !== 1 ? 's' : ''}</span>
          </button>

          {showDetails && (
            <div className="p-3 bg-gray-900/50 max-h-64 overflow-y-auto space-y-3">
              {childInsights.map((child, idx) => {
                const childSeverity = child.attributes?.severity;
                const childSeverityColors = childSeverity ? SEVERITY_COLORS[childSeverity] : null;
                return (
                  <div key={idx} className="p-2 rounded bg-gray-800/50 border border-gray-700">
                    <div className="flex items-center gap-2 mb-1">
                      <Lightbulb className="w-3 h-3 text-purple-400" />
                      {child.attributes?.type && (
                        <span className="text-xs text-purple-400">[{child.attributes.type}]</span>
                      )}
                      {childSeverity && childSeverityColors && (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${childSeverityColors.bg} ${childSeverityColors.text} border ${childSeverityColors.border}`}>
                          {childSeverity}
                        </span>
                      )}
                      {child.attributes?.date && (
                        <span className="text-xs text-gray-500">{child.attributes.date}</span>
                      )}
                    </div>
                    {child.content && (
                      <p className="text-xs text-gray-300 mt-1 line-clamp-3">{child.content}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* For other tag types: show attributes */}
      {firstTag.tagType !== 'insight-result' && (
        <div className="border-t border-gray-700/50">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs bg-gray-900/30 hover:bg-gray-900/50 text-gray-400 transition-colors"
          >
            {showDetails ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            <span>Details</span>
            <span className="text-gray-600">({Object.keys(firstTag.attributes || {}).length} attributes)</span>
          </button>

          {showDetails && (
            <div className="p-3 bg-gray-900/50 max-h-48 overflow-y-auto">
              {insightTags.map((tag, idx) => (
                <div key={idx} className={`${idx > 0 ? 'mt-3 pt-3 border-t border-gray-700' : ''}`}>
                  {insightTags.length > 1 && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-gray-500">{INSIGHT_CONFIG[tag.tagType]?.label || tag.tagType}</span>
                    </div>
                  )}
                  <div className="space-y-1">
                    {Object.entries(tag.attributes || {}).map(([key, value]) => (
                      <div key={key} className="flex items-start gap-2 text-xs font-mono">
                        <span className="text-purple-400 flex-shrink-0">{key}:</span>
                        <span className="text-green-400 break-all">{value}</span>
                      </div>
                    ))}
                    {tag.content && (
                      <div className="mt-2 pt-2 border-t border-gray-700">
                        <span className="text-xs text-gray-500">Content:</span>
                        <pre className="text-xs text-gray-300 mt-1 whitespace-pre-wrap">{tag.content}</pre>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Compact system feature indicator - shows when agent uses help, query, or receives session context
 * Styled like ChatActivityIndicator but for system features
 * For session-context: clicking expands to show SessionContextCard
 */
function SystemFeatureIndicator({ featureType, details, sessionContext }) {
  const [expanded, setExpanded] = useState(false);

  // Determine text and icon based on feature type
  let text = '';
  let IconComponent = Info;
  let colorClass = 'text-cyan-400 hover:text-cyan-300';

  switch (featureType) {
    case 'help':
      text = 'Uses help system';
      IconComponent = HelpCircle;
      colorClass = 'text-cyan-400 hover:text-cyan-300';
      break;
    case 'query':
      const queryType = details?.queryType || 'system';
      const queryLabels = {
        channels: 'Queries channels',
        members: 'Queries members',
        status: 'Queries status',
        queue: 'Queries message queue'
      };
      text = queryLabels[queryType] || `Queries ${queryType}`;
      IconComponent = Search;
      colorClass = 'text-blue-400 hover:text-blue-300';
      break;
    case 'session-context':
      text = 'Session context received';
      IconComponent = Zap;
      colorClass = 'text-amber-400 hover:text-amber-300';
      break;
    case 'auto-reconnect':
      text = 'Session reconnected after backend restart';
      IconComponent = Zap;
      colorClass = 'text-green-400 hover:text-green-300';
      break;
    case 'context-warning':
      const percentUsed = details?.percentUsed || '?';
      const threshold = details?.threshold || '?';
      text = `Context warning: ${percentUsed}% used (threshold: ${threshold}%)`;
      IconComponent = AlertTriangle;
      colorClass = 'text-amber-400 hover:text-amber-300';
      break;
    case 'self-note':
      text = 'Self-note saved';
      IconComponent = StickyNote;
      colorClass = 'text-blue-400 hover:text-blue-300';
      // Self-note has expandable content
      if (details?.content) {
        return (
          <div className="flex flex-col items-center gap-1 py-1">
            <button
              onClick={() => setExpanded(!expanded)}
              className={`flex items-center gap-2 text-xs ${colorClass} transition-colors cursor-pointer`}
            >
              <IconComponent className="w-3.5 h-3.5" />
              <span>{text}</span>
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </button>
            {expanded && <SelfNoteCard content={details.content} />}
          </div>
        );
      }
      break;
    case 'chat-error':
      const errorLabels = {
        'channel_not_found': 'Channel not found',
        'recipient_not_found': 'Recipient not found',
        'agent_not_member': 'Not a channel member',
        'session_not_registered': 'Session not registered',
        'parse_error': 'Invalid chat format',
        'forbidden': 'Chat forbidden',
        'other': 'Chat error'
      };
      const errorType = details?.errorType || 'other';
      text = errorLabels[errorType] || 'Chat error';
      if (details?.message) {
        text += `: ${details.message}`;
      }
      IconComponent = AlertCircle;
      colorClass = 'text-red-400 hover:text-red-300';
      break;
    case 'system-error':
      // System errors from backend (task validation, tag processing errors)
      const errorCode = details?.code || 'E500';
      const errorTagType = details?.type || 'unknown';
      const errorMessage = details?.message || 'Unknown error';
      text = `${errorCode}: ${errorMessage}`;
      IconComponent = AlertCircle;
      colorClass = 'text-red-500 hover:text-red-400';
      // Show type as subtitle if it's a task error
      if (errorTagType.startsWith('task-')) {
        return (
          <div className="flex flex-col items-center gap-1 py-2">
            <div className={`flex items-center gap-2 text-xs ${colorClass}`}>
              <IconComponent className="w-4 h-4" />
              <span className="font-medium">{text}</span>
            </div>
            <span className="text-xs text-gray-500">Tag: &lt;{errorTagType}/&gt;</span>
          </div>
        );
      }
      break;
    case 'credentials-list':
      // Credential list operation
      IconComponent = Key;
      if (details?.success) {
        const count = details?.count || 0;
        const scope = details?.scope;
        text = scope
          ? `Listed ${count} credential${count !== 1 ? 's' : ''} (${scope})`
          : `Listed ${count} credential${count !== 1 ? 's' : ''}`;
        colorClass = 'text-indigo-400 hover:text-indigo-300';
      } else {
        text = `Credentials list failed: ${details?.error || 'Unknown error'}`;
        colorClass = 'text-red-400 hover:text-red-300';
      }
      break;
    case 'credentials-get':
      // Credential get operation
      IconComponent = Key;
      if (details?.success) {
        const credName = details?.name || 'unknown';
        const credType = details?.credentialType;
        text = credType
          ? `Loaded credential: ${credName} (${credType})`
          : `Loaded credential: ${credName}`;
        colorClass = 'text-indigo-400 hover:text-indigo-300';
      } else {
        // Check for server configuration errors (E500) vs credential-specific errors
        const isServerError = details?.errorCode === 'E500' || details?.error?.includes('not configured');
        if (isServerError) {
          text = `Credentials error: ${details?.error || 'Server configuration error'}`;
        } else {
          const credName = details?.name || 'unknown';
          text = `Credential not found: ${credName}`;
          if (details?.error && credName !== 'unknown') {
            text += ` - ${details.error}`;
          }
        }
        colorClass = 'text-red-400 hover:text-red-300';
      }
      break;
    default:
      text = 'System feature used';
  }

  // For session-context, make it clickable to show SessionContextCard
  if (featureType === 'session-context' && sessionContext) {
    return (
      <div className="flex flex-col items-center gap-1 py-1">
        <button
          onClick={() => setExpanded(!expanded)}
          className={`flex items-center gap-2 text-xs ${colorClass} transition-colors cursor-pointer`}
        >
          <IconComponent className="w-3.5 h-3.5" />
          <span>{text}</span>
          {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </button>
        {expanded && <SessionContextCard sessionContext={sessionContext} />}
      </div>
    );
  }

  // Credentials indicators get special styling to stand out
  if (featureType === 'credentials-list' || featureType === 'credentials-get') {
    return (
      <div className="w-full py-2 my-2 bg-indigo-900/50 border border-indigo-500 rounded-lg text-center" data-testid="credentials-indicator">
        <div className={`flex items-center justify-center gap-2 text-sm font-medium ${colorClass}`}>
          <IconComponent className="w-4 h-4" />
          <span>{text}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-1 py-1">
      <div className={`flex items-center gap-2 text-xs ${colorClass} transition-colors`}>
        <IconComponent className="w-3.5 h-3.5" />
        <span>{text}</span>
      </div>
    </div>
  );
}

/**
 * SelfNoteCard - Displays self-note content when expanded
 * Shows the content the agent saved before context compaction
 */
function SelfNoteCard({ content }) {
  if (!content) return null;

  return (
    <div className="flex justify-center py-2">
      <div className="inline-block max-w-lg bg-gradient-to-br from-blue-900/30 to-indigo-900/30 border border-blue-500/30 rounded-xl shadow-lg shadow-blue-500/10 overflow-hidden">
        <div className="px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
              <StickyNote className="w-3 h-3 text-blue-400" />
            </div>
            <span className="text-xs text-gray-500 uppercase tracking-wide">Self-Note Content</span>
          </div>
          <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">
            {content}
          </div>
        </div>
      </div>
    </div>
  );
}

function ThinkingBlock({ thinking }) {
  const [show, setShow] = useState(false);

  if (!thinking || thinking.length === 0) return null;

  return (
    <div className="mb-3">
      <button
        onClick={() => setShow(!show)}
        className="flex items-center gap-2 text-xs text-purple-400 hover:text-purple-300 transition-colors"
      >
        <Brain className="w-3.5 h-3.5" />
        <span>Thinking</span>
        {show ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>
      {show && (
        <div className="mt-2 p-3 bg-purple-900/10 border border-purple-500/20 rounded-lg">
          {thinking.map((thought, idx) => (
            <div key={idx} className="text-sm text-purple-300/80 italic whitespace-pre-wrap">
              {thought}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Status info display component (non-collapsible info badge)
// Rendered as a centered info line, similar to system messages
function StatusInfo({ statusTags }) {
  if (!statusTags || statusTags.length === 0) return null;

  // Mode display names and colors (matches backend StatusUpdateSchema)
  const modeConfig = {
    ready: { label: 'Ready', color: 'text-green-400' },
    working: { label: 'Working', color: 'text-yellow-400' },
    focus: { label: 'Focus', color: 'text-orange-400' },
    in_call: { label: 'In Call', color: 'text-blue-400' },
    offline: { label: 'Offline', color: 'text-gray-400' },
  };

  return (
    <div className="flex items-center gap-2 justify-center py-1">
      {statusTags.map((tag, idx) => {
        const config = modeConfig[tag.mode];
        return (
          <div key={idx} className="flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs text-gray-500">Status set to</span>
            <span className={`text-xs font-medium ${config.color}`}>{config.label}</span>
            {tag.project && (
              <span className="text-xs text-gray-600">({tag.project})</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Queue info display component
// Shows when agent is reading next queued message
function QueueInfo({ count }) {
  if (!count || count === 0) return null;

  return (
    <div className="flex items-center gap-2 justify-center py-1">
      <div className="flex items-center gap-1.5">
        <Info className="w-3.5 h-3.5 text-gray-500" />
        <span className="text-xs text-gray-500">Reading next queued message</span>
      </div>
    </div>
  );
}

// ============================================
// Session Context Card Component
// ============================================

/**
 * SessionContextCard - Displays session context info after compact/session start
 * Shows agent identity, channel memberships, queue status
 */
function SessionContextCard({ sessionContext }) {
  const [expanded, setExpanded] = useState(false);

  if (!sessionContext) return null;

  const { agent, role, team, channels, queue, status, hint, selfNote } = sessionContext;

  // Mode display names and colors
  const modeConfig = {
    ready: { label: 'Ready', color: 'text-green-400', bg: 'bg-green-500/20' },
    working: { label: 'Working', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
    focus: { label: 'Focus', color: 'text-orange-400', bg: 'bg-orange-500/20' },
  };

  // Level display config
  const levelConfig = {
    executor: { label: 'Executor', color: 'text-gray-400', bg: 'bg-gray-500/20' },
    contributor: { label: 'Contributor', color: 'text-green-400', bg: 'bg-green-500/20' },
    lead: { label: 'Lead', color: 'text-purple-400', bg: 'bg-purple-500/20' },
    owner: { label: 'Owner', color: 'text-amber-400', bg: 'bg-amber-500/20' },
  };

  const statusConfig = modeConfig[status?.mode] || modeConfig.ready;

  return (
    <div className="flex justify-center py-2">
      <div className="inline-block max-w-lg bg-gradient-to-br from-indigo-900/30 to-purple-900/30 border border-indigo-500/30 rounded-xl shadow-lg shadow-indigo-500/10 overflow-hidden">
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between gap-4 px-4 py-3 hover:bg-indigo-500/10 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center">
              <Bot className="w-4 h-4 text-indigo-400" />
            </div>
            <div className="text-left">
              <div className="text-sm font-medium text-indigo-300">Session Context</div>
              <div className="text-xs text-gray-400">
                {agent?.name || 'Agent'}
                {role && ` • ${role.displayName || role.name}`}
                {team && ` • Team: ${team.project}`}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {role?.level && (
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${levelConfig[role.level]?.color || 'text-gray-400'} ${levelConfig[role.level]?.bg || 'bg-gray-500/20'}`}>
                {levelConfig[role.level]?.label || role.level}
              </span>
            )}
            {status && (
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusConfig.color} ${statusConfig.bg}`}>
                {statusConfig.label}
              </span>
            )}
            {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          </div>
        </button>

        {/* Expanded Content */}
        {expanded && (
          <div className="px-4 pb-4 pt-2 border-t border-indigo-500/20 space-y-3">
            {/* Agent Info */}
            {agent && (
              <div className="space-y-1">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Agent</div>
                <div className="flex items-center gap-2">
                  <code className="text-xs text-indigo-300 bg-gray-800/50 px-2 py-1 rounded font-mono">{agent.uuid}</code>
                  {agent.name && <span className="text-sm text-gray-300">{agent.name}</span>}
                </div>
                {agent.description && (
                  <p className="text-xs text-gray-400 leading-relaxed mt-1">{agent.description}</p>
                )}
              </div>
            )}

            {/* Role */}
            {role && (
              <div className="space-y-1">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Role</div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-purple-300 font-medium">{role.displayName || role.name}</span>
                    {role.level && (
                      <span className={`px-1.5 py-0.5 rounded text-xs ${levelConfig[role.level]?.color || 'text-gray-400'} ${levelConfig[role.level]?.bg || 'bg-gray-500/20'}`}>
                        {levelConfig[role.level]?.label || role.level}
                      </span>
                    )}
                  </div>
                  {role.description && (
                    <p className="text-xs text-gray-400 leading-relaxed">{role.description}</p>
                  )}
                </div>
              </div>
            )}

            {/* Team */}
            {team && team.members?.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Team ({team.project})</div>
                <div className="space-y-1">
                  {team.members.map((member, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <span className={member.type === 'user' ? 'text-blue-400' : 'text-indigo-400'}>
                        {member.name}
                      </span>
                      {member.role && (
                        <>
                          <span className="text-gray-500">•</span>
                          <span className="text-gray-400">{member.role}</span>
                        </>
                      )}
                      {member.level && (
                        <span className={`px-1.5 py-0.5 rounded text-xs ${levelConfig[member.level]?.color || 'text-gray-400'} ${levelConfig[member.level]?.bg || 'bg-gray-500/20'}`}>
                          {levelConfig[member.level]?.label || member.level}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Channels */}
            {channels.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Channels</div>
                <div className="space-y-1">
                  {channels.map((ch, idx) => {
                    const count = ch.memberCount ?? ch.members?.length ?? 0;
                    return (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <span className="text-purple-400">#{ch.name || ch.displayName}</span>
                        <span className="text-gray-500">•</span>
                        <span className="text-gray-400">{count} member{count !== 1 ? 's' : ''}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Queue */}
            {queue && (
              <div className="space-y-1">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Message Queue</div>
                <div className="text-sm text-gray-300">
                  {queue.pending === 0 ? (
                    <span className="text-green-400">No pending messages</span>
                  ) : (
                    <span className="text-yellow-400">{queue.pending} message{queue.pending !== 1 ? 's' : ''} queued</span>
                  )}
                </div>
              </div>
            )}

            {/* Hint */}
            {hint && (
              <div className="text-xs text-gray-500 italic border-t border-indigo-500/20 pt-2 mt-2">
                {hint.replace(/&lt;/g, '<').replace(/&gt;/g, '>')}
              </div>
            )}

            {/* Self-Note */}
            {selfNote && (
              <div className="space-y-1 border-t border-blue-500/20 pt-3 mt-3">
                <div className="flex items-center gap-2">
                  <StickyNote className="w-3.5 h-3.5 text-blue-400" />
                  <span className="text-xs text-gray-500 uppercase tracking-wide">Self-Note</span>
                </div>
                <div className="text-sm text-blue-300/90 whitespace-pre-wrap leading-relaxed bg-blue-900/20 rounded-lg p-3 border border-blue-500/20">
                  {selfNote}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Help Response Card Component
// ============================================

/**
 * HelpResponseCard - Displays available commands and their usage
 */
function HelpResponseCard({ helpResponse }) {
  const [expandedCommands, setExpandedCommands] = useState(new Set());

  if (!helpResponse) return null;

  const { agent, commands } = helpResponse;

  const toggleCommand = (name) => {
    setExpandedCommands(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <div className="flex justify-center py-2">
      <div className="inline-block max-w-2xl bg-gradient-to-br from-cyan-900/30 to-blue-900/30 border border-cyan-500/30 rounded-xl shadow-lg shadow-cyan-500/10 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-cyan-500/20">
          <div className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
            <HelpCircle className="w-4 h-4 text-cyan-400" />
          </div>
          <div>
            <div className="text-sm font-medium text-cyan-300">Available Commands</div>
            {agent && (
              <div className="text-xs text-gray-400">
                Your Agent: <code className="text-cyan-400">{agent.name || agent.uuid}</code>
              </div>
            )}
          </div>
        </div>

        {/* Commands List */}
        <div className="divide-y divide-cyan-500/10">
          {commands.map((cmd, idx) => (
            <div key={idx} className="px-4 py-2">
              <button
                onClick={() => toggleCommand(cmd.name)}
                className="w-full flex items-center justify-between text-left hover:bg-cyan-500/5 -mx-2 px-2 py-1 rounded transition-colors"
              >
                <div className="flex items-center gap-2">
                  <code className="text-sm font-medium text-cyan-400">&lt;{cmd.name}&gt;</code>
                  <span className="text-xs text-gray-400">{cmd.description}</span>
                </div>
                {expandedCommands.has(cmd.name) ? (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                )}
              </button>

              {expandedCommands.has(cmd.name) && (
                <div className="mt-2 pl-2 space-y-2 text-xs">
                  {/* Syntax */}
                  {cmd.syntax && (
                    <div>
                      <div className="text-gray-500 mb-1">Syntax:</div>
                      <pre className="text-cyan-300 bg-gray-800/50 p-2 rounded overflow-x-auto font-mono">
                        {cmd.syntax}
                      </pre>
                    </div>
                  )}

                  {/* Example */}
                  {cmd.example && (
                    <div>
                      <div className="text-gray-500 mb-1">Example:</div>
                      <pre className="text-green-300 bg-gray-800/50 p-2 rounded overflow-x-auto font-mono">
                        {cmd.example}
                      </pre>
                    </div>
                  )}

                  {/* Modes */}
                  {cmd.modes && (
                    <div>
                      <div className="text-gray-500 mb-1">Modes:</div>
                      <div className="space-y-1">
                        {cmd.modes.map((mode, mIdx) => (
                          <div key={mIdx} className="flex items-start gap-2">
                            <code className="text-yellow-400 bg-gray-800/50 px-1 rounded">{mode.name}</code>
                            <span className="text-gray-400">{mode.description}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Types */}
                  {cmd.types && (
                    <div>
                      <div className="text-gray-500 mb-1">Types:</div>
                      <div className="space-y-1">
                        {cmd.types.map((type, tIdx) => (
                          <div key={tIdx} className="flex items-start gap-2">
                            <code className="text-purple-400 bg-gray-800/50 px-1 rounded">{type.name}</code>
                            <span className="text-gray-400">{type.description}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Note */}
                  {cmd.note && (
                    <div className="text-gray-400 italic border-l-2 border-cyan-500/30 pl-2">
                      {cmd.note}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Query Response Card Component
// ============================================

/**
 * QueryResponseCard - Displays query results (channels, members, status, queue)
 */
function QueryResponseCard({ queryResponse }) {
  if (!queryResponse) return null;

  const { type } = queryResponse;

  // Mode display config
  const modeConfig = {
    ready: { label: 'Ready', color: 'text-green-400' },
    working: { label: 'Working', color: 'text-yellow-400' },
    focus: { label: 'Focus', color: 'text-orange-400' },
  };

  const renderContent = () => {
    switch (type) {
      case 'channels':
        return (
          <div className="space-y-2">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Your Channels</div>
            {queryResponse.channels.map((ch, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm bg-gray-800/30 px-3 py-2 rounded">
                <span className="text-emerald-400">#{ch.name || ch.displayName}</span>
                {ch.memberCount && (
                  <span className="text-xs text-gray-500">{ch.memberCount} members</span>
                )}
              </div>
            ))}
          </div>
        );

      case 'members':
        return (
          <div className="space-y-2">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Channel Members</div>
            {queryResponse.members.map((m, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm bg-gray-800/30 px-3 py-2 rounded">
                <div className="flex items-center gap-2">
                  {m.type === 'agent' ? (
                    <Bot className="w-4 h-4 text-indigo-400" />
                  ) : (
                    <User className="w-4 h-4 text-emerald-400" />
                  )}
                  <span className="text-gray-300">{m.name}</span>
                </div>
                {m.status && (
                  <span className={`text-xs ${modeConfig[m.status]?.color || 'text-gray-500'}`}>
                    {modeConfig[m.status]?.label || m.status}
                  </span>
                )}
              </div>
            ))}
          </div>
        );

      case 'status':
        return (
          <div className="space-y-3">
            {queryResponse.agent && (
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Agent</div>
                <div className="text-sm text-gray-300">{queryResponse.agent.name || queryResponse.agent.uuid}</div>
              </div>
            )}
            {queryResponse.status && (
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Status</div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${modeConfig[queryResponse.status.mode]?.color || 'text-gray-300'}`}>
                    {modeConfig[queryResponse.status.mode]?.label || queryResponse.status.mode}
                  </span>
                  {queryResponse.status.project && (
                    <span className="text-xs text-gray-500">on {queryResponse.status.project}</span>
                  )}
                </div>
              </div>
            )}
            {queryResponse.queue && (
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Queue</div>
                <div className="text-sm text-gray-300">{queryResponse.queue.pending} pending</div>
              </div>
            )}
          </div>
        );

      case 'queue':
        return (
          <div className="space-y-3">
            {queryResponse.summary && (
              <div className="flex items-center justify-between text-sm bg-gray-800/30 px-3 py-2 rounded">
                <span className="text-gray-400">Total pending:</span>
                <span className={queryResponse.summary.pending > 0 ? 'text-yellow-400 font-medium' : 'text-green-400'}>
                  {queryResponse.summary.pending}
                </span>
              </div>
            )}
            {queryResponse.byChannel.length > 0 && (
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">By Channel</div>
                {queryResponse.byChannel.map((ch, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm bg-gray-800/30 px-3 py-2 rounded mb-1">
                    <span className="text-emerald-400">#{ch.name}</span>
                    <span className="text-gray-400">{ch.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return <div className="text-gray-500 text-sm">Unknown query type: {type}</div>;
    }
  };

  const typeLabels = {
    channels: 'Channels',
    members: 'Members',
    status: 'Status',
    queue: 'Queue'
  };

  // Use static class names for Tailwind (dynamic classes don't work with JIT)
  const typeStyles = {
    channels: {
      gradient: 'from-emerald-900/30 to-gray-900/30',
      border: 'border-emerald-500/30',
      shadow: 'shadow-emerald-500/10',
      iconBg: 'bg-emerald-500/20',
      iconBorder: 'border-emerald-500/30',
      iconText: 'text-emerald-400',
      title: 'text-emerald-300'
    },
    members: {
      gradient: 'from-blue-900/30 to-gray-900/30',
      border: 'border-blue-500/30',
      shadow: 'shadow-blue-500/10',
      iconBg: 'bg-blue-500/20',
      iconBorder: 'border-blue-500/30',
      iconText: 'text-blue-400',
      title: 'text-blue-300'
    },
    status: {
      gradient: 'from-purple-900/30 to-gray-900/30',
      border: 'border-purple-500/30',
      shadow: 'shadow-purple-500/10',
      iconBg: 'bg-purple-500/20',
      iconBorder: 'border-purple-500/30',
      iconText: 'text-purple-400',
      title: 'text-purple-300'
    },
    queue: {
      gradient: 'from-yellow-900/30 to-gray-900/30',
      border: 'border-yellow-500/30',
      shadow: 'shadow-yellow-500/10',
      iconBg: 'bg-yellow-500/20',
      iconBorder: 'border-yellow-500/30',
      iconText: 'text-yellow-400',
      title: 'text-yellow-300'
    }
  };

  const styles = typeStyles[type] || typeStyles.status;

  return (
    <div className="flex justify-center py-2">
      <div className={`inline-block max-w-md bg-gradient-to-br ${styles.gradient} border ${styles.border} rounded-xl shadow-lg ${styles.shadow} overflow-hidden`}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-700/50">
          <div className={`w-8 h-8 rounded-lg ${styles.iconBg} border ${styles.iconBorder} flex items-center justify-center`}>
            <Search className={`w-4 h-4 ${styles.iconText}`} />
          </div>
          <div className={`text-sm font-medium ${styles.title}`}>
            Query: {typeLabels[type] || type}
          </div>
        </div>
        <div className="px-4 py-3">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

function SystemHints({ reminders, inline = false }) {
  const [show, setShow] = useState(false);

  if (!reminders || reminders.length === 0) return null;

  if (inline) {
    return (
      <span className="inline-flex items-center gap-1 ml-2">
        <button
          onClick={() => setShow(!show)}
          className="inline-flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 transition-colors"
        >
          <Info className="w-3 h-3" />
          <span>{reminders.length} hint{reminders.length > 1 ? 's' : ''}</span>
          {show ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </button>
      </span>
    );
  }

  return (
    <>
      <button
        onClick={() => setShow(!show)}
        className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 transition-colors mt-2"
      >
        <Info className="w-3 h-3" />
        <span>{reminders.length} system hint{reminders.length > 1 ? 's' : ''}</span>
        {show ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>
      {show && (
        <div className="mt-2 border-t border-purple-500/20 bg-purple-900/10 rounded-lg p-2.5">
          {reminders.map((reminder, idx) => (
            <div key={idx} className="text-xs text-purple-300/70 font-mono whitespace-pre-wrap mb-2 last:mb-0">
              <span className="text-purple-400 font-semibold">System Hint {idx + 1}:</span>
              <div className="mt-1 pl-2 border-l-2 border-purple-500/30 max-h-32 overflow-y-auto">
                {reminder}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ============================================
// Tool Configuration
// ============================================

const TOOL_CONFIG = {
  Read: { icon: FileText, color: 'blue', label: 'Reading file', getSummary: (input) => input.file_path?.split('/').slice(-2).join('/') || 'file' },
  Glob: { icon: FolderSearch, color: 'cyan', label: 'Searching files', getSummary: (input) => input.pattern || 'pattern' },
  Grep: { icon: Search, color: 'yellow', label: 'Searching content', getSummary: (input) => `"${input.pattern?.substring(0, 30)}${input.pattern?.length > 30 ? '...' : ''}"` || 'pattern' },
  Edit: { icon: Pencil, color: 'orange', label: 'Editing file', getSummary: (input) => input.file_path?.split('/').slice(-2).join('/') || 'file' },
  Write: { icon: FilePlus, color: 'green', label: 'Writing file', getSummary: (input) => input.file_path?.split('/').slice(-2).join('/') || 'file' },
  Bash: { icon: Terminal, color: 'purple', label: 'Running command', getSummary: (input) => input.description || (input.command?.length > 50 ? input.command.substring(0, 50) + '...' : input.command) },
  WebFetch: { icon: Globe, color: 'pink', label: 'Fetching URL', getSummary: (input) => { try { return new URL(input.url || '').hostname; } catch { return input.url?.substring(0, 30) || 'url'; } } },
  WebSearch: { icon: Globe, color: 'cyan', label: 'Web search', getSummary: (input) => `"${input.query?.substring(0, 40)}${input.query?.length > 40 ? '...' : ''}"` || 'query' },
  Task: { icon: Play, color: 'indigo', label: 'Running agent', getSummary: (input) => input.description || input.subagent_type || 'task' },
  TodoWrite: { icon: CheckCircle, color: 'green', label: 'Updating todos', getSummary: (input) => { const todos = Array.isArray(input.todos) ? input.todos : []; const inProgress = todos.filter(t => t.status === 'in_progress').length; const completed = todos.filter(t => t.status === 'completed').length; return `${completed}/${todos.length} done${inProgress ? `, ${inProgress} active` : ''}`; } },
  NotebookEdit: { icon: FileText, color: 'orange', label: 'Editing notebook', getSummary: (input) => input.notebook_path?.split('/').pop() || 'notebook' },
  KillShell: { icon: Terminal, color: 'red', label: 'Killing shell', getSummary: (input) => input.shell_id || 'shell' },
  TaskOutput: { icon: Play, color: 'indigo', label: 'Getting task output', getSummary: (input) => input.task_id?.substring(0, 8) || 'task' },
  AskUserQuestion: { icon: HelpCircle, color: 'yellow', label: 'Asking question', getSummary: (input) => { const questions = input.questions || []; if (questions.length === 1) return questions[0].header || 'question'; return `${questions.length} questions`; }, isInteractive: true },
  ExitPlanMode: { icon: ClipboardList, color: 'purple', label: 'Plan Review', getSummary: () => 'Awaiting approval', isInteractive: true },
  EnterPlanMode: { icon: Brain, color: 'purple', label: 'Entering Plan Mode', getSummary: () => 'Planning...' },
  Skill: { icon: Zap, color: 'purple', label: 'Launching skill', getSummary: (input) => input.skill || 'skill' },
  SlashCommand: { icon: Command, color: 'cyan', label: 'Running command', getSummary: (input) => input.command?.split(' ')[0] || 'command' },
};

// ============================================
// Tool Cards
// ============================================

const ToolUseCard = memo(function ToolUseCard({ tool, input, result, onSendMessage }) {
  const [showResult, setShowResult] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});

  const config = TOOL_CONFIG[tool] || { icon: Terminal, color: 'purple', label: tool, getSummary: () => 'executing...' };
  const colors = COLOR_CLASSES[config.color] || COLOR_CLASSES.purple;
  const Icon = config.icon;
  const summary = config.getSummary(input);

  const resultData = useMemo(() => {
    if (!result) return null;
    let raw = '';
    const content = result.content;
    if (content === undefined || content === null) raw = '(no content)';
    else if (typeof content === 'string') raw = content;
    else if (Array.isArray(content)) raw = content.map(c => typeof c === 'string' ? c : c?.type === 'text' ? c.text || '' : JSON.stringify(c)).join('\n');
    else raw = JSON.stringify(content, null, 2);
    const parsed = parseSystemReminders(raw);
    return { contentStr: parsed.content, systemReminders: parsed.reminders, isSuccess: !result.isError, chars: parsed.content.length };
  }, [result]);

  const formatInput = () => typeof input === 'string' ? input : JSON.stringify(input, null, 2);

  // Special input rendering for different tool types
  const renderSpecialInput = () => {
    // Edit - diff view
    if (tool === 'Edit' && input.old_string && input.new_string) {
      const oldLines = input.old_string.split('\n');
      const newLines = input.new_string.split('\n');
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs group/path">
            <span className="text-gray-500">File:</span>
            <code className="text-orange-400">{input.file_path}</code>
            <CopyButton text={input.file_path} className="opacity-0 group-hover/path:opacity-100" />
          </div>
          <div className="rounded overflow-hidden border border-gray-700 text-xs font-mono">
            <div className="bg-red-900/30">
              {oldLines.map((line, i) => (
                <div key={`old-${i}`} className="flex">
                  <span className="w-8 text-right pr-2 text-red-400/50 select-none border-r border-gray-700 bg-red-900/20">{i + 1}</span>
                  <span className="w-6 text-center text-red-400 select-none">-</span>
                  <span className="flex-1 text-red-300 pl-1 whitespace-pre-wrap break-all">{line || ' '}</span>
                </div>
              ))}
            </div>
            <div className="bg-green-900/30 border-t border-gray-700">
              {newLines.map((line, i) => (
                <div key={`new-${i}`} className="flex">
                  <span className="w-8 text-right pr-2 text-green-400/50 select-none border-r border-gray-700 bg-green-900/20">{i + 1}</span>
                  <span className="w-6 text-center text-green-400 select-none">+</span>
                  <span className="flex-1 text-green-300 pl-1 whitespace-pre-wrap break-all">{line || ' '}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="text-red-400">-{oldLines.length} lines</span>
            <span className="text-green-400">+{newLines.length} lines</span>
          </div>
        </div>
      );
    }

    // Bash command
    if (tool === 'Bash' && input.command) {
      return (
        <div className="space-y-2">
          <LazyCodeBlock language="bash" style={oneDark} customStyle={{ margin: 0, padding: '8px', fontSize: '11px', borderRadius: '4px' }}>
            {input.command}
          </LazyCodeBlock>
          {input.description && <div className="text-xs text-gray-500">Description: {input.description}</div>}
        </div>
      );
    }

    // Grep
    if (tool === 'Grep') {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Pattern:</span>
            <code className="text-xs text-yellow-400 bg-gray-800 px-2 py-0.5 rounded">{input.pattern}</code>
          </div>
          {input.path && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Path:</span>
              <code className="text-xs text-gray-300">{input.path}</code>
            </div>
          )}
          {input.glob && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Glob:</span>
              <code className="text-xs text-gray-300">{input.glob}</code>
            </div>
          )}
          {input.output_mode && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Mode:</span>
              <code className="text-xs text-gray-300">{input.output_mode}</code>
            </div>
          )}
        </div>
      );
    }

    // Glob
    if (tool === 'Glob') {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Pattern:</span>
            <code className="text-xs text-cyan-400 bg-gray-800 px-2 py-0.5 rounded">{input.pattern}</code>
          </div>
          {input.path && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Path:</span>
              <code className="text-xs text-gray-300">{input.path}</code>
            </div>
          )}
        </div>
      );
    }

    // Read
    if (tool === 'Read') {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 group/path">
            <span className="text-xs text-gray-500">File:</span>
            <code className="text-xs text-blue-400">{input.file_path}</code>
            <CopyButton text={input.file_path} className="opacity-0 group-hover/path:opacity-100" />
          </div>
          {(input.offset || input.limit) && (
            <div className="flex items-center gap-4">
              {input.offset && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Offset:</span>
                  <code className="text-xs text-gray-300">{input.offset}</code>
                </div>
              )}
              {input.limit && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Limit:</span>
                  <code className="text-xs text-gray-300">{input.limit}</code>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    // TodoWrite
    if (tool === 'TodoWrite' && input.todos) {
      return (
        <div className="space-y-1">
          {input.todos.map((todo, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className={`w-2 h-2 rounded-full ${todo.status === 'completed' ? 'bg-green-500' : todo.status === 'in_progress' ? 'bg-yellow-500' : 'bg-gray-500'}`} />
              <span className={todo.status === 'completed' ? 'text-gray-500 line-through' : 'text-gray-300'}>{todo.content}</span>
            </div>
          ))}
        </div>
      );
    }

    // WebSearch
    if (tool === 'WebSearch') {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Query:</span>
            <code className="text-xs text-cyan-400 bg-gray-800 px-2 py-0.5 rounded">{input.query}</code>
          </div>
          {input.allowed_domains && input.allowed_domains.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Domains:</span>
              <code className="text-xs text-green-400">{input.allowed_domains.join(', ')}</code>
            </div>
          )}
          {input.blocked_domains && input.blocked_domains.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Blocked:</span>
              <code className="text-xs text-red-400">{input.blocked_domains.join(', ')}</code>
            </div>
          )}
        </div>
      );
    }

    // WebFetch
    if (tool === 'WebFetch') {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">URL:</span>
            <code className="text-xs text-pink-400 bg-gray-800 px-2 py-0.5 rounded truncate max-w-xs">{input.url}</code>
          </div>
          {input.prompt && (
            <div className="space-y-1">
              <span className="text-xs text-gray-500">Prompt:</span>
              <div className="text-xs text-gray-300 bg-gray-800 px-2 py-1 rounded">
                {input.prompt.length > 150 ? input.prompt.substring(0, 150) + '...' : input.prompt}
              </div>
            </div>
          )}
        </div>
      );
    }

    // Task
    if (tool === 'Task') {
      return (
        <div className="space-y-2">
          {input.subagent_type && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Agent:</span>
              <code className="text-xs text-indigo-400 bg-gray-800 px-2 py-0.5 rounded">{input.subagent_type}</code>
            </div>
          )}
          {input.description && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Task:</span>
              <span className="text-xs text-gray-300">{input.description}</span>
            </div>
          )}
          {input.prompt && (
            <div className="space-y-1">
              <span className="text-xs text-gray-500">Prompt:</span>
              <div className="text-xs text-gray-300 bg-gray-800 px-2 py-1 rounded max-h-24 overflow-y-auto">
                {input.prompt.length > 300 ? input.prompt.substring(0, 300) + '...' : input.prompt}
              </div>
            </div>
          )}
          {input.model && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Model:</span>
              <code className="text-xs text-purple-400">{input.model}</code>
            </div>
          )}
        </div>
      );
    }

    // Write
    if (tool === 'Write') {
      const fileName = input.file_path?.split('/').pop() || 'file';
      const extension = fileName.split('.').pop() || '';
      const langMap = {
        js: 'javascript', jsx: 'jsx', ts: 'typescript', tsx: 'tsx',
        py: 'python', rb: 'ruby', go: 'go', rs: 'rust',
        json: 'json', yaml: 'yaml', yml: 'yaml', md: 'markdown',
        css: 'css', scss: 'scss', html: 'html', sh: 'bash',
      };
      const language = langMap[extension] || 'text';
      const lines = (input.content || '').split('\n').length;
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs group/path">
            <span className="text-gray-500">File:</span>
            <code className="text-green-400">{input.file_path}</code>
            <CopyButton text={input.file_path} className="opacity-0 group-hover/path:opacity-100" />
            <span className="text-gray-600">•</span>
            <span className="text-gray-500">{lines} lines</span>
          </div>
          {input.content && (
            <LazyCodeBlock
              language={language}
              style={oneDark}
              customStyle={{ margin: 0, padding: '8px', fontSize: '11px', borderRadius: '4px', maxHeight: '200px' }}
              showLineNumbers={true}
              wrapLongLines={true}
            >
              {input.content}
            </LazyCodeBlock>
          )}
        </div>
      );
    }

    // Skill
    if (tool === 'Skill') {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-medium text-purple-400">{input.skill}</span>
          </div>
          {input.args && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Args:</span>
              <code className="text-xs text-gray-300">{input.args}</code>
            </div>
          )}
        </div>
      );
    }

    // SlashCommand
    if (tool === 'SlashCommand') {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Command className="w-4 h-4 text-cyan-400" />
            <code className="text-sm text-cyan-400 bg-gray-800 px-2 py-0.5 rounded">{input.command}</code>
          </div>
        </div>
      );
    }

    // TaskOutput - getting output from background task
    if (tool === 'TaskOutput') {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Play className="w-4 h-4 text-indigo-400" />
            <span className="text-xs text-gray-500">Task ID:</span>
            <code className="text-xs text-indigo-400 bg-gray-800 px-2 py-0.5 rounded font-mono">{input.task_id}</code>
          </div>
          <div className="flex items-center gap-4 text-xs">
            {input.block !== undefined && (
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Blocking:</span>
                <span className={input.block ? 'text-yellow-400' : 'text-gray-400'}>{input.block ? 'Yes' : 'No'}</span>
              </div>
            )}
            {input.timeout && (
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Timeout:</span>
                <span className="text-gray-300">{input.timeout}ms</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    // KillShell - terminating a background shell
    if (tool === 'KillShell') {
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Terminal className="w-4 h-4 text-red-400" />
            <span className="text-xs text-gray-500">Shell ID:</span>
            <code className="text-xs text-red-400 bg-gray-800 px-2 py-0.5 rounded font-mono">{input.shell_id}</code>
          </div>
          <div className="text-xs text-red-400/70 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            <span>Terminating background process</span>
          </div>
        </div>
      );
    }

    // ExitPlanMode - plan review/approval
    if (tool === 'ExitPlanMode') {
      const hasResult = result && !result.isError;
      const wasApproved = hasResult && result.content && !result.content.includes('rejected');

      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-purple-400" />
            <span className="text-sm font-medium text-purple-300">Implementation Plan Ready</span>
          </div>
          <p className="text-xs text-gray-400">
            Claude has finished planning and is ready to implement. Review the plan above and approve to continue.
          </p>
          {!hasResult && onSendMessage && (
            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={() => onSendMessage('yes')}
                className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Approve Plan
              </button>
              <button
                onClick={() => onSendMessage('no')}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm font-medium rounded-lg transition-colors"
              >
                Reject
              </button>
            </div>
          )}
          {hasResult && (
            <div className={`flex items-center gap-2 pt-2 text-sm ${wasApproved ? 'text-green-400' : 'text-red-400'}`}>
              {wasApproved ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              <span>{wasApproved ? 'Plan approved - implementing...' : 'Plan rejected'}</span>
            </div>
          )}
        </div>
      );
    }

    // AskUserQuestion - interactive UI (prominent, scrollable)
    if (tool === 'AskUserQuestion') {
      const questions = input.questions || [];
      const hasResult = result && !result.isError;

      const handleOptionClick = (qIdx, optIdx, isMulti) => {
        if (hasResult) return;
        setSelectedOptions(prev => {
          const key = `q${qIdx}`;
          if (isMulti) {
            const current = prev[key] || [];
            if (current.includes(optIdx)) return { ...prev, [key]: current.filter(i => i !== optIdx) };
            return { ...prev, [key]: [...current, optIdx] };
          }
          return { ...prev, [key]: [optIdx] };
        });
      };

      const isOptionSelected = (qIdx, optIdx) => (selectedOptions[`q${qIdx}`] || []).includes(optIdx);
      const hasSelection = Object.values(selectedOptions).some(arr => arr.length > 0);

      const handleSubmit = () => {
        if (!onSendMessage || hasResult) return;
        const answers = questions.map((q, qIdx) => {
          const selected = selectedOptions[`q${qIdx}`] || [];
          const labels = selected.map(idx => q.options[idx]?.label).filter(Boolean);
          return labels.join(', ') || '';
        }).filter(Boolean);
        if (answers.length > 0) onSendMessage(answers.join('\n'));
      };

      return (
        <div className="flex flex-col max-h-[70vh]">
          {/* Header - fixed */}
          <div className="flex items-center gap-3 pb-4 border-b border-yellow-500/30 mb-4 flex-shrink-0">
            <div className="w-10 h-10 rounded-xl bg-yellow-500/20 border border-yellow-500/30 flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-yellow-300">Question from Claude</h3>
              <p className="text-xs text-gray-500">{questions.length} question{questions.length > 1 ? 's' : ''} • Select your answer{questions.length > 1 ? 's' : ''}</p>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto space-y-6 pr-2 min-h-0">
            {questions.map((q, qIdx) => (
              <div key={qIdx} className="space-y-4">
                {/* Question header badge */}
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1.5 bg-yellow-500/20 text-yellow-400 text-sm font-semibold rounded-lg border border-yellow-500/30">
                    {q.header}
                  </span>
                  {q.multiSelect && (
                    <span className="text-xs text-gray-400 bg-gray-800 px-2 py-1 rounded">select multiple</span>
                  )}
                </div>

                {/* Question text - larger and more prominent */}
                <p className="text-base text-gray-100 leading-relaxed font-medium">{q.question}</p>

                {/* Options - larger with better spacing */}
                <div className="grid gap-3">
                  {q.options?.map((opt, optIdx) => {
                    const isSelected = isOptionSelected(qIdx, optIdx);
                    return (
                      <button
                        key={optIdx}
                        onClick={() => handleOptionClick(qIdx, optIdx, q.multiSelect)}
                        disabled={hasResult}
                        className={`w-full flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all duration-200 ease-out
                          ${hasResult ? 'bg-gray-800/30 border-gray-700 opacity-60 cursor-not-allowed'
                            : isSelected ? 'bg-yellow-500/15 border-yellow-500/60 shadow-lg shadow-yellow-500/10 scale-[1.01]'
                            : 'bg-gray-800/40 border-gray-700 hover:border-yellow-500/40 hover:bg-gray-800/60 hover:scale-[1.005]'}`}
                      >
                        {/* Checkbox/Radio - larger */}
                        <div className="mt-0.5 transition-transform duration-200 flex-shrink-0">
                          {q.multiSelect ? (
                            <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${isSelected ? 'border-yellow-500 bg-yellow-500 scale-110' : 'border-gray-500 bg-gray-700'}`}>
                              {isSelected && <CheckCircle className="w-4 h-4 text-gray-900" />}
                            </div>
                          ) : (
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${isSelected ? 'border-yellow-500 bg-yellow-500 scale-110' : 'border-gray-500 bg-gray-700'}`}>
                              {isSelected && <div className="w-3 h-3 rounded-full bg-gray-900" />}
                            </div>
                          )}
                        </div>
                        {/* Option content - larger text */}
                        <div className="flex-1 min-w-0">
                          <div className={`text-base font-medium transition-colors duration-200 ${isSelected ? 'text-yellow-300' : 'text-gray-100'}`}>
                            {opt.label}
                          </div>
                          {opt.description && (
                            <div className="text-sm text-gray-400 mt-1.5 leading-relaxed">{opt.description}</div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Footer - fixed */}
          <div className="pt-4 mt-4 border-t border-gray-700 flex-shrink-0">
            {!hasResult && onSendMessage && (
              <button
                onClick={handleSubmit}
                disabled={!hasSelection}
                className={`w-full py-3.5 rounded-xl text-base font-semibold transition-all duration-200 ${hasSelection ? 'bg-yellow-500 text-gray-900 hover:bg-yellow-400 shadow-lg shadow-yellow-500/25 hover:shadow-yellow-500/40' : 'bg-gray-700 text-gray-500 cursor-not-allowed'}`}
              >
                Submit Answer
              </button>
            )}
            {hasResult && (
              <div className="flex items-center justify-center gap-2 py-3 text-base text-green-400 bg-green-500/10 rounded-xl border border-green-500/30">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Response submitted</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Default JSON view
    return <LazyCodeBlock language="json" style={oneDark} customStyle={{ margin: 0, padding: '8px', fontSize: '11px', borderRadius: '4px' }}>{formatInput()}</LazyCodeBlock>;
  };

  const isInteractiveTool = config.isInteractive;
  const cardWidthClass = isInteractiveTool ? 'max-w-[90%]' : 'inline-block max-w-[85%]';

  // For interactive tools, render without the standard card wrapper - width adapts to content
  if (isInteractiveTool) {
    return (
      <div className={`ml-11 inline-block rounded-xl border-2 ${colors.border} ${colors.bg} overflow-hidden transition-all duration-200 shadow-lg ${colors.glow} ${cardWidthClass}`}>
        <div className="p-5 bg-gray-900/50">
          {renderSpecialInput()}
        </div>
      </div>
    );
  }

  return (
    <div className={`ml-11 rounded-lg border ${colors.border} ${colors.bg} overflow-hidden transition-all duration-200 shadow-lg ${colors.glow} ${cardWidthClass}`}>
      <div className="flex items-center gap-3 p-2.5">
        <div className={`w-7 h-7 rounded-md ${colors.bg} border ${colors.border} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${colors.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium ${colors.text}`}>{config.label}</span>
            <span className="text-gray-600">-</span>
            <code className="text-xs text-gray-300 truncate">{summary}</code>
            {resultData && (
              <>
                <span className="text-gray-600">-</span>
                <span className={`inline-flex items-center gap-1 text-xs ${resultData.isSuccess ? 'text-green-400' : 'text-red-400'}`}>
                  {resultData.isSuccess ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                  {resultData.isSuccess ? 'OK' : 'Error'}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="border-t border-gray-700/50 p-3 bg-gray-900/50 overflow-x-auto max-h-48 overflow-y-auto">
        {renderSpecialInput()}
      </div>
      {resultData && !isInteractiveTool && (
        <div className="border-t border-gray-700/50">
          <button onClick={() => setShowResult(!showResult)} className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${resultData.isSuccess ? 'bg-green-500/5 hover:bg-green-500/10 text-green-400' : 'bg-red-500/5 hover:bg-red-500/10 text-red-400'}`}>
            {showResult ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            <span>Result</span>
            <span className="text-gray-500">({resultData.chars} chars)</span>
          </button>
          {showResult && (
            <div className={`p-3 max-h-48 overflow-y-auto ${resultData.isSuccess ? 'bg-green-500/5' : 'bg-red-500/5'}`}>
              <pre className="text-xs text-gray-400 whitespace-pre-wrap font-mono">{resultData.contentStr}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

const ToolResultCard = memo(function ToolResultCard({ content, isSuccess = true }) {
  const parsedResult = useMemo(() => {
    let raw = '';
    if (content === undefined || content === null) raw = '(no content)';
    else if (typeof content === 'string') raw = content;
    else if (Array.isArray(content)) raw = content.map(c => typeof c === 'string' ? c : c?.type === 'text' ? c.text || '' : JSON.stringify(c)).join('\n');
    else raw = JSON.stringify(content, null, 2);

    const withoutReminders = parseSystemReminders(raw);

    // Check for system tags in tool result content
    const withoutSessionContext = parseSessionContext(withoutReminders.contentStr || withoutReminders.content || raw);
    const withoutHelpResponse = parseHelpResponse(withoutSessionContext.content);
    const withoutQueryResponses = parseQueryResponse(withoutHelpResponse.content);

    return {
      contentStr: withoutQueryResponses.content,
      sessionContext: withoutSessionContext.sessionContext,
      helpResponse: withoutHelpResponse.helpResponse,
      queryResponses: withoutQueryResponses.queryResponses
    };
  }, [content]);

  // If we have system tags, render them specially instead of as tool result
  const hasSystemContent = parsedResult.sessionContext || parsedResult.helpResponse || (parsedResult.queryResponses?.length > 0);

  if (hasSystemContent) {
    return (
      <div className="space-y-1">
        {parsedResult.sessionContext && <SystemFeatureIndicator featureType="session-context" sessionContext={parsedResult.sessionContext} />}
        {parsedResult.helpResponse && <SystemFeatureIndicator featureType="help" />}
        {parsedResult.queryResponses?.map((qr, idx) => (
          <SystemFeatureIndicator key={idx} featureType="query" details={{ queryType: qr.type }} />
        ))}
        {parsedResult.contentStr && parsedResult.contentStr.trim() && (
          <div className={`ml-11 rounded-lg border overflow-hidden inline-block max-w-[85%] ${isSuccess ? 'border-green-500/30 bg-green-500/10' : 'border-red-500/30 bg-red-500/10'}`}>
            <div className="border-t border-gray-700/50 bg-gray-900/50 max-h-48 overflow-y-auto">
              <pre className="text-xs text-gray-400 whitespace-pre-wrap p-3 font-mono">{parsedResult.contentStr}</pre>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`ml-11 rounded-lg border overflow-hidden inline-block max-w-[85%] shadow-lg ${isSuccess ? 'border-green-500/30 bg-green-500/10 shadow-green-500/20' : 'border-red-500/30 bg-red-500/10 shadow-red-500/20'}`}>
      <div className="flex items-center gap-3 p-2.5">
        <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${isSuccess ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
          {isSuccess ? <CheckCircle className="w-4 h-4 text-green-400" /> : <AlertCircle className="w-4 h-4 text-red-400" />}
        </div>
        <span className={`text-xs font-medium ${isSuccess ? 'text-green-400' : 'text-red-400'}`}>{isSuccess ? 'Success' : 'Error'}</span>
      </div>
      {parsedResult.contentStr && (
        <div className="border-t border-gray-700/50 bg-gray-900/50 max-h-48 overflow-y-auto">
          <pre className="text-xs text-gray-400 whitespace-pre-wrap p-3 font-mono">{parsedResult.contentStr}</pre>
        </div>
      )}
    </div>
  );
});

// ============================================
// Message Component
// ============================================

const Message = memo(function Message({ message, onSendMessage, agentName = 'Claude', agentAvatar = null, userAvatar = null, rawModeEnabled = false }) {
  const { type, content, tool, input, attachments, responseType, timestamp } = message;

  // Raw mode - show JSON structure for debugging
  if (rawModeEnabled) {
    return <RawMessageView message={message} />;
  }

  const parsedContent = useMemo(() => {
    // DEBUG: Log all message content to see what's coming in
    if (content && (content.includes('help-response') || content.includes('query-response') || content.includes('session-context'))) {
      console.log('[Message] System tag found in', type, 'message:', content.substring(0, 200));
    }

    if (type === 'assistant') {
      const withoutReminders = parseSystemReminders(content);
      const withoutThinking = parseThinking(withoutReminders.content);
      const withoutStatus = parseStatusTags(withoutThinking.content);
      const withoutQueue = parseQueueTags(withoutStatus.content);
      const withoutChatTags = parseChatTags(withoutQueue.content);
      // Strip system request tags (<help/>, <query .../>) - these are agent requests, not user-visible
      const withoutRequestTags = parseSystemRequestTags(withoutChatTags.content);
      // Parse new system message tags
      const withoutSessionContext = parseSessionContext(withoutRequestTags.content);
      const withoutHelpResponse = parseHelpResponse(withoutSessionContext.content);
      const withoutQueryResponses = parseQueryResponse(withoutHelpResponse.content);
      // Parse system-error tags (injected by backend for validation errors)
      const withoutSystemErrors = parseSystemErrors(withoutQueryResponses.content);
      // Parse credentials tags (injected by backend when agent accesses credentials)
      const withoutCredentials = parseCredentialsTags(withoutSystemErrors.content);


      // DEBUG
      if (withoutHelpResponse.helpResponse) {
        console.log('[Message] Parsed helpResponse:', withoutHelpResponse.helpResponse);
      }
      if (withoutQueryResponses.queryResponses?.length > 0) {
        console.log('[Message] Parsed queryResponses:', withoutQueryResponses.queryResponses);
      }

      return {
        content: withoutCredentials.content,
        reminders: withoutReminders.reminders,
        thinking: withoutThinking.thinking,
        statusTags: withoutStatus.statusTags,
        queueTags: withoutQueue.queueTags,
        chatTags: withoutChatTags.chatTags,
        // New system message data
        sessionContext: withoutSessionContext.sessionContext,
        helpResponse: withoutHelpResponse.helpResponse,
        queryResponses: withoutQueryResponses.queryResponses,
        systemErrors: withoutSystemErrors.systemErrors,
        credentialOps: withoutCredentials.credentialOps
      };
    }
    if (type === 'user') {
      // Parse incoming chat-message tags and system-reminders from user messages
      const withoutReminders = parseSystemReminders(content);
      const withoutChatMessages = parseChatMessageTags(withoutReminders.content);
      // Also parse session context in user messages (injected by backend)
      const withoutSessionContext = parseSessionContext(withoutChatMessages.content);
      // Parse context-warning tags (injected by backend when threshold reached)
      const withoutContextWarning = parseContextWarning(withoutSessionContext.content);
      const withoutHelpResponse = parseHelpResponse(withoutContextWarning.content);
      const withoutQueryResponses = parseQueryResponse(withoutHelpResponse.content);
      // Parse system-error tags (injected by backend for validation errors)
      const withoutSystemErrors = parseSystemErrors(withoutQueryResponses.content);
      // Parse credentials tags (injected by backend when agent accesses credentials)
      const withoutCredentials = parseCredentialsTags(withoutSystemErrors.content);


      // DEBUG
      if (withoutHelpResponse.helpResponse) {
        console.log('[Message] Parsed helpResponse (user):', withoutHelpResponse.helpResponse);
      }
      if (withoutQueryResponses.queryResponses?.length > 0) {
        console.log('[Message] Parsed queryResponses (user):', withoutQueryResponses.queryResponses);
      }
      return {
        content: withoutCredentials.content,
        reminders: withoutReminders.reminders,
        chatMessages: withoutChatMessages.chatMessages,
        hasOnlyChatMessages: withoutChatMessages.hasOnlyChatMessages,
        // New system message data (can be in user messages via injection)
        sessionContext: withoutSessionContext.sessionContext,
        contextWarning: withoutContextWarning.contextWarning,
        helpResponse: withoutHelpResponse.helpResponse,
        queryResponses: withoutQueryResponses.queryResponses,
        systemErrors: withoutSystemErrors.systemErrors,
        credentialOps: withoutCredentials.credentialOps
      };
    }
    return null;
  }, [type, content]);

  const renderContent = () => {
    switch (type) {
      case 'user':
        // Check if this is a chat message (from the chat system)
        const userParsed = parsedContent;
        const hasChatMessages = userParsed?.chatMessages?.length > 0;
        const displayContent = userParsed?.content || content;
        const hasUserSystemMessages = userParsed?.sessionContext || userParsed?.contextWarning || userParsed?.helpResponse || userParsed?.queryResponses?.length > 0 || userParsed?.systemErrors?.length > 0 || userParsed?.credentialOps?.length > 0;

        // If message only contains system tags (session context, context warning, help, query, system-error), render only those
        if (hasUserSystemMessages && !displayContent?.trim() && !hasChatMessages) {
          return (
            <>
              {userParsed.sessionContext && <SessionContextCard sessionContext={userParsed.sessionContext} />}
              {userParsed.contextWarning && <SystemFeatureIndicator featureType="context-warning" details={userParsed.contextWarning} />}
              {userParsed.helpResponse && <HelpResponseCard helpResponse={userParsed.helpResponse} />}
              {userParsed.queryResponses?.map((qr, idx) => (
                <QueryResponseCard key={idx} queryResponse={qr} />
              ))}
              {userParsed.systemErrors?.map((err, idx) => (
                <SystemFeatureIndicator key={`err-${idx}`} featureType="system-error" details={err} />
              ))}
              {userParsed.credentialOps?.map((op, idx) => (
                op.type === 'list'
                  ? <CredentialsListIndicator key={`cred-${idx}`} details={op} />
                  : <CredentialsGetIndicator key={`cred-${idx}`} details={op} />
              ))}
            </>
          );
        }

        // If message only contains chat-messages, render them as compact badges
        // This prevents duplicate rendering (chat messages are also shown in "Processing..." badges)
        if (userParsed?.hasOnlyChatMessages && hasChatMessages) {
          return (
            <div className="flex flex-col items-center gap-1 py-1">
              {userParsed.chatMessages.map((chatMsg, idx) => (
                <div key={chatMsg.uuid || idx} className="flex items-center gap-2 text-xs text-emerald-400">
                  <MessageCircle className="w-3.5 h-3.5" />
                  <span className="font-medium">{chatMsg.from?.name || 'Chat'}</span>
                  <span className="text-gray-500">sent a message</span>
                </div>
              ))}
            </div>
          );
        }

        // Regular user message (possibly with some chat messages mixed in)
        return (
          <>
            {/* Show system message cards first */}
            {userParsed?.sessionContext && <SessionContextCard sessionContext={userParsed.sessionContext} />}
            {userParsed?.helpResponse && <HelpResponseCard helpResponse={userParsed.helpResponse} />}
            {userParsed?.queryResponses?.map((qr, idx) => (
              <QueryResponseCard key={idx} queryResponse={qr} />
            ))}
            {userParsed?.systemErrors?.map((err, idx) => (
              <SystemFeatureIndicator key={`err-${idx}`} featureType="system-error" details={err} />
            ))}
            {userParsed?.credentialOps?.map((op, idx) => (
              op.type === 'list'
                ? <CredentialsListIndicator key={`cred-${idx}`} details={op} />
                : <CredentialsGetIndicator key={`cred-${idx}`} details={op} />
            ))}
            {/* Then show the regular user message if there's content */}
            {(displayContent?.trim() || hasChatMessages || attachments?.length > 0) && (
              <div className="flex gap-3">
                {userAvatar ? (
                  <img src={userAvatar} alt="You" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4" />
                  </div>
                )}
                <div className="max-w-[85%]">
                  <div className="text-xs text-gray-500 mb-1">You{timestamp ? ` · ${formatMessageTime(timestamp)}` : ''}</div>
                  <div className="bg-gray-800 rounded-lg rounded-tl-none p-3 text-gray-100 inline-block">
                    {attachments && attachments.length > 0 && (
                      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-700">
                        <Paperclip className="w-3.5 h-3.5 text-orange-400" />
                        <span className="text-xs text-orange-400">{attachments.length} file(s)</span>
                      </div>
                    )}
                    {/* Show chat messages first if any */}
                    {hasChatMessages && (
                      <div className="mb-2 pb-2 border-b border-gray-700">
                        {userParsed.chatMessages.map((chatMsg, idx) => (
                          <div key={chatMsg.uuid || idx} className="flex items-start gap-2 text-xs mb-1 last:mb-0">
                            <MessageCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <span className="text-emerald-400 font-medium">{chatMsg.from?.name || 'Chat'}:</span>
                              <span className="text-gray-300 ml-1">{chatMsg.content}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {displayContent && <pre className="whitespace-pre-wrap font-sans text-sm">{displayContent}</pre>}
                  </div>
                </div>
              </div>
            )}
          </>
        );

      case 'assistant':
        const { content: cleanContent, reminders, thinking, statusTags, queueTags, sessionContext, helpResponse, queryResponses, systemErrors, credentialOps } = parsedContent;
        const hasSystemMessages = sessionContext || helpResponse || queryResponses?.length > 0 || systemErrors?.length > 0 || credentialOps?.length > 0;

        // If only control tags or system messages (no actual text content), show just those
        const hasOnlyControlTags = !cleanContent?.trim() && (statusTags?.length > 0 || queueTags > 0 || hasSystemMessages);
        if (hasOnlyControlTags) {
          return (
            <>
              {statusTags?.length > 0 && <StatusInfo statusTags={statusTags} />}
              {queueTags > 0 && <QueueInfo count={queueTags} />}
              {sessionContext && <SessionContextCard sessionContext={sessionContext} />}
              {helpResponse && <HelpResponseCard helpResponse={helpResponse} />}
              {queryResponses?.map((qr, idx) => (
                <QueryResponseCard key={idx} queryResponse={qr} />
              ))}
              {systemErrors?.map((err, idx) => (
                <SystemFeatureIndicator key={`err-${idx}`} featureType="system-error" details={err} />
              ))}
              {credentialOps?.map((op, idx) => (
                op.type === 'list'
                  ? <CredentialsListIndicator key={`cred-${idx}`} details={op} />
                  : <CredentialsGetIndicator key={`cred-${idx}`} details={op} />
              ))}
            </>
          );
        }

        return (
          <>
            {/* Show status/queue info above the message bubble */}
            {statusTags?.length > 0 && <StatusInfo statusTags={statusTags} />}
            {queueTags > 0 && <QueueInfo count={queueTags} />}
            {/* Show system message cards */}
            {sessionContext && <SessionContextCard sessionContext={sessionContext} />}
            {helpResponse && <HelpResponseCard helpResponse={helpResponse} />}
            {queryResponses?.map((qr, idx) => (
              <QueryResponseCard key={idx} queryResponse={qr} />
            ))}
            {systemErrors?.map((err, idx) => (
              <SystemFeatureIndicator key={`err-${idx}`} featureType="system-error" details={err} />
            ))}
            {credentialOps?.map((op, idx) => (
              op.type === 'list'
                ? <CredentialsListIndicator key={`cred-${idx}`} details={op} />
                : <CredentialsGetIndicator key={`cred-${idx}`} details={op} />
            ))}
            {/* Show assistant message bubble if there's actual content */}
            {cleanContent.trim() && (
              <div className="flex gap-3">
                {agentAvatar ? (
                  <img src={agentAvatar} alt={agentName} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4" />
                  </div>
                )}
                <div className="max-w-[85%]">
                  <div className="text-xs text-gray-500 mb-1">{agentName}{timestamp ? ` · ${formatMessageTime(timestamp)}` : ''}</div>
                  <div className="bg-gray-850 rounded-lg rounded-tl-none p-4 border border-gray-700 inline-block">
                    <ThinkingBlock thinking={thinking} />
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        code({ node, inline, className, children, ...props }) {
                          const match = /language-(\w+)/.exec(className || '');
                          const codeString = String(children).replace(/\n$/, '');
                          if (!inline && match) {
                            return <LazyCodeBlock style={oneDark} language={match[1]} PreTag="div" customStyle={{ margin: 0, borderRadius: '0.5rem', fontSize: '0.75rem' }} {...props}>{codeString}</LazyCodeBlock>;
                          }
                          return <code className={`${className || ''} bg-gray-800 px-1.5 py-0.5 rounded text-orange-300`} {...props}>{children}</code>;
                        }
                      }}
                    >
                      {cleanContent}
                    </ReactMarkdown>
                    <SystemHints reminders={reminders} />
                  </div>
                </div>
              </div>
            )}
          </>
        );

      case 'tool_use':
        return <ToolUseCard tool={tool} input={input} result={message._pairedResult} onSendMessage={onSendMessage} />;

      case 'tool_result':
        return <ToolResultCard content={content} isSuccess={!message.isError} />;

      case 'system':
        return (
          <div className="flex items-center gap-2 justify-center">
            <Info className="w-4 h-4 text-gray-500" />
            <span className="text-xs text-gray-500">{content}</span>
          </div>
        );

      case 'system_response': {
        // Handle special responseTypes first
        if (responseType === 'auto-reconnect') {
          return (
            <div className="space-y-1">
              <SystemFeatureIndicator featureType="auto-reconnect" />
            </div>
          );
        }

        // Handle context-warning responseType
        if (responseType === 'context-warning') {
          // Values come directly from the message (set in AgentChatContext)
          const percentUsed = message.percentUsed;
          const threshold = message.threshold;
          return (
            <div className="space-y-1">
              <SystemFeatureIndicator featureType="context-warning" details={{ percentUsed, threshold }} />
            </div>
          );
        }

        // Handle self-note responseType
        if (responseType === 'self-note') {
          let selfNoteContent;
          try {
            const parsed = typeof content === 'string' ? JSON.parse(content) : content;
            selfNoteContent = parsed?.content;
          } catch {
            // Content might be the note itself
            selfNoteContent = content;
          }
          return (
            <div className="space-y-1">
              <SystemFeatureIndicator featureType="self-note" details={{ content: selfNoteContent }} />
            </div>
          );
        }

        // Handle task responseType (task-created, task-deleted, etc.)
        if (responseType === 'task') {
          const { taskTags } = parseTaskResponse(content || '');
          if (taskTags.length > 0) {
            return <TaskActivityIndicator taskTags={taskTags} />;
          }
          // Fallback if no tags parsed
          return null;
        }

        // Handle insight-query responseType (insight-result from semantic search)
        if (responseType === 'insight-query') {
          console.log('[MessageList] insight-query system_response content:', content);
          const { insightTags } = parseInsightTags(content || '');
          console.log('[MessageList] parsed insightTags:', insightTags);
          if (insightTags.length > 0) {
            return <InsightActivityIndicator insightTags={insightTags} />;
          }
          // Fallback: render raw content if parsing fails
          if (content) {
            return (
              <div className="ml-11 p-3 rounded-lg bg-green-900/20 border border-green-500/30 text-sm text-gray-300">
                <div className="text-xs text-green-400 mb-1">Insight Result</div>
                <pre className="whitespace-pre-wrap text-xs">{content}</pre>
              </div>
            );
          }
          return null;
        }

        // Handle chat-error responseType
        if (responseType === 'chat-error') {
          return (
            <div className="space-y-1">
              <SystemFeatureIndicator
                featureType="chat-error"
                details={{
                  errorType: message.errorType,
                  code: message.code,
                  message: message.message
                }}
              />
            </div>
          );
        }

        // Handle credentials responseTypes (credentials-get, credentials-get-error, credentials-list, credentials-list-error)
        // Guido sends responseType: 'credentials-get', 'credentials-list', 'credentials-get-error', etc.
        if (responseType?.startsWith('credentials-')) {
          const isError = responseType.endsWith('-error');
          const isGet = responseType.includes('-get');
          const isList = responseType.includes('-list');

          // Extract name, type, encrypted from attributes OR from content XML as fallback
          // Backend may not always include attributes, but content has the XML with name="...", type="...", encrypted="..."
          let credName = message.attributes?.name;
          let credType = message.attributes?.type;
          let credEncrypted = message.attributes?.encrypted;

          if (content) {
            // HTML decode content for attribute extraction
            const decodedForAttrs = content
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&quot;/g, '"')
              .replace(/&amp;/g, '&');

            if (!credName) {
              const nameMatch = /name="([^"]*)"/.exec(decodedForAttrs);
              if (nameMatch) credName = nameMatch[1];
            }
            if (!credType) {
              const typeMatch = /type="([^"]*)"/.exec(decodedForAttrs);
              if (typeMatch) credType = typeMatch[1];
            }
            if (credEncrypted === undefined) {
              const encryptedMatch = /encrypted="([^"]*)"/.exec(decodedForAttrs);
              if (encryptedMatch) credEncrypted = encryptedMatch[1] === 'true';
            }
          }

          // Extract count and credential details for list operations from content XML
          // The XML contains <credential .../> elements that need to be counted and parsed
          let credCount = message.count;
          const credentials = []; // Array of { name, type } for expand view

          if (isList && content) {
            // HTML decode content before parsing - backend may HTML-escape the credential tags
            const decodedContent = content
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&quot;/g, '"')
              .replace(/&amp;/g, '&');

            // Extract credential details from child elements
            const credentialRegex = /<credential\s+([^>]*)\/?>/gi;
            let credMatch;
            while ((credMatch = credentialRegex.exec(decodedContent)) !== null) {
              const credAttrs = credMatch[1];
              const nameMatch = /name="([^"]*)"/.exec(credAttrs);
              const typeMatch = /type="([^"]*)"/.exec(credAttrs);
              if (nameMatch) {
                credentials.push({
                  name: nameMatch[1],
                  type: typeMatch ? typeMatch[1] : null
                });
              }
            }

            // Use parsed count if not provided
            if (!credCount) {
              credCount = credentials.length;
            }
          }

          // Build credentialOp object from message fields
          // Note: SystemFeatureIndicator expects 'success' boolean, not 'status' string
          const credentialOp = {
            success: !isError,
            name: credName,
            credentialType: credType,  // For get operations - expand view
            encrypted: credEncrypted,  // For get operations - expand view
            error: message.errorMessage,
            errorCode: message.errorCode,  // Needed for E500 detection in SystemFeatureIndicator
            // For list operations
            count: credCount,
            credentials, // Include parsed credential details for expand view
            scope: message.scope
          };

          return (
            <div className="space-y-1">
              {isList ? (
                <CredentialsListIndicator details={credentialOp} />
              ) : (
                <CredentialsGetIndicator details={credentialOp} />
              )}
            </div>
          );
        }

        // Render compact status indicators instead of large cards
        const { helpResponse } = parseHelpResponse(content || '');
        const { queryResponses } = parseQueryResponse(content || '');
        const { sessionContext } = parseSessionContext(content || '');

        return (
          <div className="space-y-1">
            {sessionContext && <SystemFeatureIndicator featureType="session-context" sessionContext={sessionContext} />}
            {helpResponse && <SystemFeatureIndicator featureType="help" />}
            {queryResponses?.map((qr, idx) => (
              <SystemFeatureIndicator key={idx} featureType="query" details={{ queryType: qr.type }} />
            ))}
          </div>
        );
      }

      case 'chat_activity':
        return <ChatActivityIndicator chatTags={message.chatTags} />;

      case 'task_activity':
        return <TaskActivityIndicator taskTags={message.taskTags} />;

      case 'insight_activity':
        return <InsightActivityIndicator insightTags={message.insightTags} />;

      case 'batched_messages':
        return <BatchedMessagesIndicator batchedMessages={message.batchedMessages} />;

      case 'chat_received':
        // Minimal indicator for received chat messages (these are shown via chat_activity already)
        return null;

      case 'error':
        return (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-lg bg-red-600/30 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-4 h-4 text-red-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-red-400 mb-1">Error</div>
              <div className="bg-red-900/20 rounded-lg p-3 text-red-300 border border-red-900/50 text-sm">{content}</div>
            </div>
          </div>
        );

      default:
        return <div className="text-xs text-gray-500 ml-11">Unknown message type: {type}</div>;
    }
  };

  return <div>{renderContent()}</div>;
});

// ============================================
// Main MessageList Component
// ============================================

const messageListPropsAreEqual = (prev, next) => {
  if (prev.messages.length !== next.messages.length) return false;
  if (prev.messages.length > 0 && next.messages.length > 0) {
    const prevLast = prev.messages[prev.messages.length - 1];
    const nextLast = next.messages[next.messages.length - 1];
    if (prevLast.content !== nextLast.content) return false;
    if (prevLast.type !== nextLast.type) return false;
  }
  if (prev.rawModeEnabled !== next.rawModeEnabled) return false;
  return prev.isProcessing === next.isProcessing && prev.onSendMessage === next.onSendMessage && prev.agentId === next.agentId;
};

export const MessageList = memo(function MessageList({ messages, isProcessing, onSendMessage, agentId, agentName, agentAvatar, userAvatar, onScrollPositionChange, rawModeEnabled = false }) {
  const containerRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const prevMessageCount = useRef(messages.length);
  const userScrolledAway = useRef(false);
  const prevAgentId = useRef(agentId);
  const hasRestoredScroll = useRef(false);
  const scrollSaveTimeoutRef = useRef(null);

  // Get scroll position directly from context to ensure we get the latest value
  const { getScrollPosition } = useUI();

  useEffect(() => {
    const agentChanged = prevAgentId.current !== agentId;
    // Detect if messages were bulk-loaded (e.g., after reconnect/history restore)
    // This happens when messages go from 0 to many, or when count changes significantly
    const messagesBulkLoaded = prevMessageCount.current === 0 && messages.length > 0;

    if (agentChanged || messagesBulkLoaded) {
      setShowScrollButton(false);
      setNewMessageCount(0);
      userScrolledAway.current = false; // Reset scroll state to enable auto-scroll
      prevMessageCount.current = messages.length;
      prevAgentId.current = agentId;
      hasRestoredScroll.current = false;
    }
  }, [agentId, messages.length]);

  // Restore scroll position after messages are loaded or agent changes
  useEffect(() => {
    if (!containerRef.current || hasRestoredScroll.current || messages.length === 0) return;

    // Read position directly from context at restoration time
    // This ensures we get the latest saved position, not a stale prop value
    const savedPosition = getScrollPosition(agentId);

    if (savedPosition > 0) {
      // For virtualized lists, we need multiple attempts because:
      // 1. The virtualizer only renders items near the viewport
      // 2. scrollHeight grows as we scroll and more items are measured
      // 3. We need to keep scrolling until we reach the target position
      let attempts = 0;
      const maxAttempts = 15; // Increased max attempts
      const targetPosition = savedPosition;

      const tryRestore = () => {
        if (!containerRef.current || attempts >= maxAttempts) {
          hasRestoredScroll.current = true;
          return;
        }

        attempts++;
        containerRef.current.scrollTop = targetPosition;
        const actualPosition = containerRef.current.scrollTop;
        const scrollHeight = containerRef.current.scrollHeight;

        // If we're close enough (within 100px) or at max scroll, we're done
        if (Math.abs(actualPosition - targetPosition) < 100 || actualPosition >= scrollHeight - containerRef.current.clientHeight - 10) {
          userScrolledAway.current = true;
          setShowScrollButton(true);
          hasRestoredScroll.current = true;
        } else {
          // Not there yet - virtualizer needs to render more items
          // Wait longer between attempts to let virtualizer catch up
          setTimeout(() => requestAnimationFrame(tryRestore), 50);
        }
      };

      // Start restoration after initial render with a small delay
      setTimeout(() => requestAnimationFrame(tryRestore), 100);
    } else {
      // Scroll to bottom for new sessions
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
          userScrolledAway.current = false;
        }
        hasRestoredScroll.current = true;
      });
    }
  }, [agentId, messages.length, getScrollPosition]);

  // Save scroll position immediately on unmount (before debounce completes)
  useEffect(() => {
    return () => {
      if (scrollSaveTimeoutRef.current) {
        clearTimeout(scrollSaveTimeoutRef.current);
      }
      // Save current scroll position immediately when unmounting
      if (containerRef.current && onScrollPositionChange) {
        onScrollPositionChange(containerRef.current.scrollTop);
      }
    };
  }, [onScrollPositionChange]);

  const checkIfAtBottom = useCallback(() => {
    if (!containerRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    return scrollHeight - scrollTop - clientHeight < 150;
  }, []);

  const handleScroll = useCallback(() => {
    const atBottom = checkIfAtBottom();
    if (atBottom) {
      userScrolledAway.current = false;
      setShowScrollButton(false);
      setNewMessageCount(0);
    } else {
      userScrolledAway.current = true;
      setShowScrollButton(true);
    }
  }, [checkIfAtBottom]);

  // Process messages - useMemo handles caching based on messages reference
  const processedMessages = useMemo(() => {
    const result = [];
    const toolResultMap = new Map();
    messages.forEach(msg => { if (msg.type === 'tool_result' && msg.toolUseId) toolResultMap.set(msg.toolUseId, msg); });
    messages.forEach((msg, index) => {
      if (msg.type === 'tool_use') {
        result.push({ ...msg, _pairedResult: toolResultMap.get(msg.toolUseId) || null, _originalIndex: index });
      } else if (msg.type === 'tool_result') {
        if (!toolResultMap.has(msg.toolUseId) || !messages.some(m => m.type === 'tool_use' && m.toolUseId === msg.toolUseId)) {
          result.push({ ...msg, _originalIndex: index });
        }
      } else if (msg.type === 'assistant') {
        // Extract chat tags from assistant messages and add as separate flow elements
        const { chatTags, content: chatCleanContent } = parseChatTags(msg.content || '');
        if (chatTags && chatTags.length > 0) {
          result.push({ type: 'chat_activity', chatTags, _originalIndex: index, _isVirtual: true });
        }
        // Extract task tags from assistant messages and add as separate flow elements
        const { taskTags, content: taskCleanContent } = parseTaskTags(chatCleanContent || '');
        if (taskTags && taskTags.length > 0) {
          result.push({ type: 'task_activity', taskTags, _originalIndex: index, _isVirtual: true });
        }
        // Extract insight tags from assistant messages and add as separate flow elements
        const { insightTags, content: insightCleanContent } = parseInsightTags(taskCleanContent || '');
        if (insightTags && insightTags.length > 0) {
          result.push({ type: 'insight_activity', insightTags, _originalIndex: index, _isVirtual: true });
        }
        // Strip system tags (session-context, self-note) from assistant content
        let cleanContent = insightCleanContent
          .replace(/<session-context>[\s\S]*?<\/session-context>/gi, '')
          .replace(/<session-context\s*\/>/gi, '')
          .replace(/<self-note(?:\s+[^>]*)?>[\s\S]*?<\/self-note>/gi, '')
          .replace(/<self-note\s*\/>/gi, '')
          .trim();
        // Only add assistant message if there's actual content after removing chat and task tags
        // Use cleanContent (without chat/task tags) instead of original content
        if (cleanContent && cleanContent.trim()) {
          result.push({ ...msg, content: cleanContent, _originalIndex: index });
        }
      } else if (msg.type === 'user') {
        // Filter out user messages that only contain chat-message XML tags
        // These are incoming chat messages that are shown elsewhere
        const { hasOnlyChatMessages, content: userCleanContent } = parseChatMessageTags(msg.content || '');

        // Parse batched-messages tags (messages batched during agent generation)
        const { batchedMessages, content: batchedCleanContent } = parseBatchedMessagesTags(userCleanContent || '');
        if (batchedMessages && batchedMessages.length > 0) {
          result.push({ type: 'batched_messages', batchedMessages, _originalIndex: index, _isVirtual: true });
        }

        // Parse insight tags from user messages (insight-result is injected as user message)
        const { insightTags: userInsightTags, content: insightCleanContent } = parseInsightTags(batchedCleanContent || '');
        if (userInsightTags && userInsightTags.length > 0) {
          result.push({ type: 'insight_activity', insightTags: userInsightTags, _originalIndex: index, _isVirtual: true });
        }

        if (hasOnlyChatMessages && (!insightCleanContent || !insightCleanContent.trim())) {
          // Add as minimal "received message" indicator (only if no other content)
          result.push({ type: 'chat_received', _originalIndex: index, _isVirtual: true });
        } else if (insightCleanContent && insightCleanContent.trim()) {
          // Regular user message with actual content (after stripping batched-messages and insight tags)
          result.push({ ...msg, content: insightCleanContent, _originalIndex: index });
        }
      } else {
        result.push({ ...msg, _originalIndex: index });
      }
    });
    return result;
  }, [messages]);

  const estimateSize = useCallback((index) => {
    const msg = processedMessages[index];
    if (!msg) return 120;

    // Better size estimates based on content length
    const contentLength = msg.content?.length || 0;

    switch (msg.type) {
      case 'user':
        // User messages are usually shorter
        return Math.max(70, Math.min(300, 70 + contentLength * 0.15));
      case 'assistant':
        // Assistant messages vary a lot - estimate based on content
        return Math.max(100, Math.min(800, 100 + contentLength * 0.2));
      case 'tool_use':
        // Tool cards have consistent base size + some variation
        return 180;
      case 'tool_result':
        return 100;
      case 'system':
        return 50;
      case 'system_response':
        return 50; // Compact status indicators
      case 'chat_activity':
        return 50;
      case 'task_activity':
        return 50;
      case 'insight_activity':
        return 50;
      case 'batched_messages':
        return 50;
      case 'chat_received':
        return 0; // Hidden, no space needed
      case 'error':
        return 100;
      default:
        return 120;
    }
  }, [processedMessages]);

  // Track scroll direction for smart measurement caching
  const scrollDirectionRef = useRef('forward');
  const lastScrollTopRef = useRef(0);

  // Improved scroll handler that tracks direction and saves position
  const handleScrollWithDirection = useCallback((e) => {
    const currentScrollTop = e.target.scrollTop;
    scrollDirectionRef.current = currentScrollTop >= lastScrollTopRef.current ? 'forward' : 'backward';
    lastScrollTopRef.current = currentScrollTop;
    handleScroll();

    // Debounced save of scroll position
    if (onScrollPositionChange) {
      if (scrollSaveTimeoutRef.current) {
        clearTimeout(scrollSaveTimeoutRef.current);
      }
      scrollSaveTimeoutRef.current = setTimeout(() => {
        onScrollPositionChange(currentScrollTop);
      }, 300);
    }
  }, [handleScroll, onScrollPositionChange]);

  const virtualizer = useVirtualizer({
    count: processedMessages.length + (isProcessing ? 1 : 0),
    getScrollElement: () => containerRef.current,
    estimateSize,
    overscan: 10, // Higher overscan to reduce pop-in
    // Smart measureElement that caches when scrolling backward to prevent jumps
    measureElement: (element, entry, instance) => {
      if (!element) return estimateSize(0);

      const height = element.getBoundingClientRect().height;

      // When scrolling backward, prefer cached size to prevent jumping
      if (scrollDirectionRef.current === 'backward') {
        const indexKey = Number(element.getAttribute('data-index'));
        const cachedSize = instance.itemSizeCache.get(indexKey);
        if (cachedSize && Math.abs(cachedSize - height) < 50) {
          // Use cached size if difference is small (prevents micro-jumps)
          return cachedSize;
        }
      }

      return height;
    },
  });

  useLayoutEffect(() => {
    if (userScrolledAway.current) {
      const newCount = messages.length - prevMessageCount.current;
      if (newCount > 0) setNewMessageCount(prev => prev + newCount);
      prevMessageCount.current = messages.length;
      return;
    }
    if (containerRef.current && processedMessages.length > 0) {
      requestAnimationFrame(() => { if (containerRef.current && !userScrolledAway.current) containerRef.current.scrollTop = containerRef.current.scrollHeight; });
    }
    prevMessageCount.current = messages.length;
  }, [messages, processedMessages.length, isProcessing]);

  const lastMessageContent = messages[messages.length - 1]?.content;
  useLayoutEffect(() => {
    if (!userScrolledAway.current && containerRef.current && lastMessageContent) {
      requestAnimationFrame(() => { if (containerRef.current && !userScrolledAway.current) containerRef.current.scrollTop = containerRef.current.scrollHeight; });
    }
  }, [lastMessageContent]);

  const scrollToBottom = useCallback(() => {
    userScrolledAway.current = false;
    scrollDirectionRef.current = 'forward';
    setShowScrollButton(false);
    setNewMessageCount(0);

    if (!containerRef.current) return;

    const container = containerRef.current;

    // Use scrollToOffset for more reliable positioning with dynamic heights
    // This is the recommended approach from TanStack Virtual maintainers
    // Wrap in setTimeout to avoid flushSync warning from @tanstack/react-virtual
    const totalSize = virtualizer.getTotalSize();
    setTimeout(() => {
      virtualizer.scrollToOffset(totalSize, { align: 'end', behavior: 'smooth' });
    }, 0);

    // Fallback verification - virtualizer may miscalculate with dynamic heights
    // Use multiple checks with increasing delays for reliable bottom detection
    const verifyAndScroll = (attempt = 1) => {
      if (!containerRef.current || userScrolledAway.current) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      if (distanceFromBottom > 20 && attempt <= 3) {
        // Not at bottom yet, scroll again
        container.scrollTo({ top: scrollHeight, behavior: attempt === 1 ? 'smooth' : 'auto' });
        setTimeout(() => verifyAndScroll(attempt + 1), 150);
      }
    };

    setTimeout(() => verifyAndScroll(), 300);
  }, [virtualizer]);

  if (messages.length === 0) {
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center p-8">
        <div className="text-center text-gray-500 max-w-md">
          <Bot className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <h2 className="text-xl font-semibold mb-2">Ready to Chat</h2>
          <p className="text-sm">Session is active. Send a message to start.</p>
        </div>
      </div>
    );
  }

  const items = virtualizer.getVirtualItems();

  return (
    <div className="relative flex-1 min-h-0 overflow-hidden">
      <div ref={containerRef} onScroll={handleScrollWithDirection} className="h-full overflow-y-auto">
        <div style={{ height: `${virtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}>
          {items.map((virtualItem) => {
            const isProcessingIndicator = virtualItem.index === processedMessages.length;
            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', transform: `translateY(${virtualItem.start}px)` }}
                className="px-4 py-2"
              >
                {isProcessingIndicator ? (
                  <div className="flex items-center gap-3 text-gray-400">
                    <Bot className="w-6 h-6" />
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                ) : (
                  <Message message={processedMessages[virtualItem.index]} onSendMessage={onSendMessage} agentName={agentName} agentAvatar={agentAvatar} userAvatar={userAvatar} rawModeEnabled={rawModeEnabled} />
                )}
              </div>
            );
          })}
        </div>
      </div>
      {showScrollButton && (
        <button onClick={scrollToBottom} className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full shadow-lg transition-all duration-200 hover:scale-105 z-10">
          <ArrowDown className="w-4 h-4" />
          <span className="text-sm font-medium">{newMessageCount > 0 ? `${newMessageCount} new` : 'Back to bottom'}</span>
        </button>
      )}
    </div>
  );
}, messageListPropsAreEqual);

export default MessageList;
